'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    Chips,
    ChipsAddEvent,
    ChipsRemoveEvent
} from 'primereact/chips';
import {
    Column,
    ColumnEditorOptions
} from 'primereact/column';
import {
    DataTable,
    DataTableRowReorderEvent
} from 'primereact/datatable';
import {Tooltip} from 'primereact/tooltip';

import {
    __,
    Placeholders
} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import EnumProperty, {EnumPropertyAttributes} from '~/shared/src/ci/EnumProperty';
import Reference from '~/shared/src/ci/Reference';
import EnumReferenceProperty, {EnumReferencePropertyAttributes} from '~/shared/src/ci/EnumReferenceProperty';
import PropertySet from '~/shared/src/mm/PropertySet';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import {
    EnumValue,
    EnumUpdateValue,
    EnumUpdateValueAttributes,
    enumUpdateValueAttributes
} from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {DataDescriptorValues} from '~/shared/src/utilities/condition';
import {Style} from '~/shared/src/utilities/style';
import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {
    EnumFieldListingOrder,
    EnumFieldPresentation,
    fieldTypeToItemTypeMapping,
    FormFieldProperties,
    useHookFormDelay
} from '~/shared/src/utilities/modules/form';

import FieldWrapper, {
    defaultProps as fieldWrapperDefaultProps,
    DefaultProps as FieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    componentProps as fieldWrapperComponentProps,
    ComponentProps as FieldWrapperComponentProps,
    MessageResolver
} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import StaticContentField, {
    componentProps as staticContentFieldComponentProps,
    ComponentProps as StaticContentFieldComponentProps
} from '~/frontend/src/components/base/shared/field/StaticContentField';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {DragElement} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {fieldUtils} from '~/frontend/src/utilities/fieldUtils';
import {
    getPropertyKeyDisplayName,
    getPropertyValueDisplayName,
    getReferencePropertyKeyDisplayName,
    getReferencePropertyValueDisplayName,
    getStatusColorStyleObject
} from '~/frontend/src/utilities/renderHelpers';
import {
    getValueEditor,
    getValueSetEditor
} from '~/frontend/src/utilities/valueEditors';
import {
    enumEntryRowStyleSelectors,
    renderValue,
    resolveStyleToClassNames,
    statusColoringRenderer
} from '~/frontend/src/utilities/valueRenderers';

import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';

type FieldElusiveData = {
    keyToUniqueIdMapping : {
        [ key : string ] : string;
    };
};

const setElusiveData = origSetElusiveData as ( panelKey : string, data : FieldElusiveData ) => SetElusiveDataAction;

type TransformedValue = {
    uniqueId : string;
    originalStringKey : string;
    id : number;
    key : AttributeValue | undefined;
    value : AttributeValue | undefined;
    orderNum : number;
    statusId : number;
};
type NewIdMapping = {
    [ stringKey : string ] : string
};
type OrderBy = {
    key : 'key' | 'value' | 'orderNum';
    direction : 'asc' | 'desc';
};
type RowData = TransformedValue & {
    deleteBody? : React.ReactNode;
    keyBody? : React.ReactNode;
    valueBody? : React.ReactNode;
};
export type EnumValueErrors = {
    [ stringKey : string ] : ( {
        [ attribute : string ] : {
            propertySetId : number;
            referencePropertySetId? : never;
            errorMessage : string;
            errorPlaceholders : Placeholders;
        } | {
            propertySetId? : never;
            referencePropertySetId : number;
            errorMessage : string;
            errorPlaceholders : Placeholders;
        };
    } )
};
type GetValueEditorParams = {
    schema : Schema;
    timezone : string;
    language? : string;
};
type CacheParameters = {
    originalStringKey : string;
    statusId : number;
    appendToElementId? : string;
    disabled? : boolean;
    fieldHasError : boolean;
    fieldIsDirty : boolean;
    inputClassNames? : string[];
    key? : AttributeValue;
    value? : AttributeValue;
    language? : string;
    fieldWidth? : string;

    /**
     * The class names the conditional style of this entry resolved to - part of the cache parameters
     * so that a style reacting to something outside the entry rebuilds the cell as well.
     */
    entryStyleClassNames? : string;
};
type FieldCache = {
    [ fieldId : string ] : {
        cacheParameters : CacheParameters;
        field : JSX.Element;
    }
};
type ColumnCache = {
    cacheParameters : CacheParameters;
    column : React.ReactNode;
}
type CellCache = {
    [ cellId : string ] : {
        cacheParameters : CacheParameters;
        body : React.ReactNode;
        editor? : React.ReactNode;
    }
};
type  ColumnId = 'delete' | 'key' | 'value' | 'sort';
type ColumnsCache = {
    [columnId in ColumnId]? : {
        columnCache : ColumnCache;
        cellCache : CellCache;
    };
};
type ChipsCache = {
    [ chipId : string ] : {
        cacheParameters : CacheParameters;
        item : React.ReactNode;
        errorMessage? : string;
        isDirty : boolean;
        tabIndex : number;

        /**
         * The class names rowStyle resolved to for this entry. A chip is drawn by PrimeReact, so they
         * are applied to its element the same way the error and dirty marking is.
         */
        rowStyleClassNames : string[];
    };
};
type EnumDragElement = DragElement & {
    originalStringKey : string;
};

type DefaultProps = FieldWrapperDefaultProps & {
    schema? : typeof defaultBaseRequestParameters[ 'schema' ];
    timezone? : typeof defaultBaseRequestParameters[ 'timezone' ];

    /**
     * Can new entries been added.
     */
    extensible? : boolean;

    /**
     * The number of text rows to be displayed within the field.
     * Setting to 0 means the field will be rendered as height as it needs to fit all contents.
     */
    height? : number;

    /**
     * The max number of text rows to be displayed within the field.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * The width of the key field either as a fixed width value with unit (e.g. 100px, 14rem) or as a flex factor for the width ratio.
     */
    keyFieldWidth? : string;

    /**
     * The width of the value field either as a fixed width value with unit (e.g. 100px, 14rem) or as a flex factor for the width ratio.
     */
    valueFieldWidth? : string;

    /**
     * How to sort the keys and/or values
     */
    listingOrder? : EnumFieldListingOrder;

    /**
     * How to present the field.
     */
    presentation? : EnumFieldPresentation;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The placeholder text to show on empty fields for new entries.
     */
    placeholder? : string;

    /**
     * Is this field is disabled.
     */
    disabled? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;

    /**
     * Conditional style for the key of an entry.
     */
    keyStyle? : Style[];

    /**
     * Conditional style for the value of an entry.
     */
    valueStyle? : Style[];

    /**
     * Conditional style for a whole entry - the row in a field list or grid, the chip in the chips
     * presentation.
     */
    rowStyle? : Style[];

    /**
     * Values for resolving the conditions of <prop>keyStyle</prop>, <prop>valueStyle</prop> and
     * <prop>rowStyle</prop>.
     * dataDescriptor.alias to value mapping
     */
    valuesForEntryStyle? : DataDescriptorValues;

    /**
     * The dataDescriptor.alias for this field - it addresses the **value** of an entry, and is
     * overwritten with the value of the entry before the entry styles are resolved.
     */
    dataDescriptorAlias? : string;

    /**
     * The dataDescriptor.alias a condition on `property.<name>.key` carries - overwritten with the
     * **key** of the entry the same way.
     *
     * It cannot be derived from <prop>dataDescriptorAlias</prop> here: an alias longer than 63
     * characters is replaced by a hash of itself, so the two are unrelated strings. The Form builds
     * it with the same builder that built the alias of the field.
     */
    keyDataDescriptorAlias? : string;
};

const defaultProps : Required<DefaultProps> = {
    schema  : defaultBaseRequestParameters[ 'schema' ],
    timezone: defaultBaseRequestParameters[ 'timezone' ],

    ...fieldWrapperDefaultProps,

    extensible     : true,
    height         : 3,
    maxHeight      : 0,
    keyFieldWidth  : '1',
    valueFieldWidth: '1',
    listingOrder   : 'order',
    presentation   : 'fieldList',
    markDirty      : false,
    placeholder    : 'YN.base.form.enumField.placeholder',
    disabled       : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false,

    keyStyle           : [],
    valueStyle         : [],
    rowStyle           : [],
    valuesForEntryStyle   : {},
    dataDescriptorAlias   : '',
    keyDataDescriptorAlias: ''
};

type CommonProps = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id? : string;

    /**
     * The value.
     */
    value? : EnumValue;

    /**
     * The value to determine dirty against.
     */
    originalValue? : EnumValue;

    /**
     * The statusId for new entries.
     */
    newStatusId? : number;

    /**
     * The statusId for deleting entries.
     */
    deleteStatusId? : number;

    /**
     * Params for the key editor
     */
    keyEditorParams? : any;

    /**
     * Params for the value  editor
     */
    valueEditorParams? : any;

    tabIndex? : number;

    /**
     * The placeholders for messages translated by MessageCatalog.
     */
    messagePlaceholders? : Placeholders;

    /**
     * A resolver function for message templateStrings.
     *
     * @param templateString
     */
    messageTemplateStringResolver? : MessageResolver;

    onChange : ( value : EnumUpdateValue | undefined, originalEvent : any ) => void;

    onIntermediateChange? : ( value : EnumUpdateValue | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};
type Props = CommonProps & (
    {

        /**
         * The PropertySet ID.
         */
        propertySetId : number;

        referencePropertySetId? : never;
    } |
    {

        /**
         * The ReferencePropertySet ID.
         */
        referencePropertySetId : number;

        propertySetId? : never;
    }
    );

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


const EnumField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const dispatch : AppDispatch                                                      = useDispatch(),
              user                                                                        = useSelector( ( state : StoreState ) => state.session?.user ),
              {
                  uid,
                  appendToElementId,
                  autoCompleteRequestParameters,
                  dataDescriptorAlias,
                  keyDataDescriptorAlias,
                  deleteStatusId,
                  disabled,
                  errorMessage,
                  extensible,
                  height,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  keyEditorParams,
                  keyFieldWidth,
                  keyStyle,
                  labelPosition,
                  labelVisibility,
                  listingOrder,
                  markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  newStatusId,
                  onChange,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  presentation,
                  propertySetId,
                  pt,
                  referencePropertySetId,
                  rowStyle,
                  schema,
                  tabIndex,
                  timezone,
                  useHookForm,
                  value,
                  valueEditorParams,
                  valueFieldWidth,
                  valuesForEntryStyle,
                  valueStyle
              }                                                                           = props,
              elementId                                                                   = id ?? `${uid}_enumField`,
              elusiveData : FieldElusiveData | undefined                                  = useSelector( ( state : StoreState ) => state.elusiveData?.[ elementId ] as FieldElusiveData | undefined ),
              wrapperClassName : string                                                   = 'field mb-0 yn-subfield-wrapper',
              dragElement                                                                 = useRef<EnumDragElement | undefined>(),
              currentFieldCache                                                           = useRef<FieldCache>( {} ),
              currentColumnsCache                                                         = useRef<ColumnsCache>( {} ),
              currentChipsCache                                                           = useRef<ChipsCache>( {} ),
              appliedChipRowStyleClassNames                                               = useRef<{ [ chipId : string ] : string[] }>( {} ),
              chipsComponentRef                                                           = useRef<Chips>( null ),
              timeoutId                                                                   = useRef<NodeJS.Timeout | undefined>(),
              [ addNewEntry, setAddNewEntry ]                                             = useState<boolean>( false ),
              propertySet : PropertySet | ReferencePropertySet | undefined                = useMemo(
                  () => propertySetId !== undefined ? metaModel[ schema ].getPropertySet( propertySetId ) : metaModel[ schema ].getReferencePropertySet( referencePropertySetId ),
                  [
                      schema,
                      propertySetId,
                      referencePropertySetId
                  ]
              ),
              currentPropertySet                                                          = useRef<PropertySet | ReferencePropertySet | undefined>( propertySet ),
              showSorter : boolean                                                        = useMemo(
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  () => listingOrder === 'order' && ( propertySet?.isKeyOrdered || propertySet?.isValueOrdered || false ),
                  [
                      propertySet,
                      listingOrder
                  ]
              ),
              keyLabel : string                                                           = useMemo(
                  () => {
                      let retVal : string = '';

                      if ( propertySetId ) {
                          retVal = getPropertyKeyDisplayName( propertySetId, schema );
                      }
                      else if ( referencePropertySetId ) {
                          retVal = getReferencePropertyKeyDisplayName( referencePropertySetId, schema );
                      }

                      return retVal;
                  },
                  [
                      schema,
                      propertySetId,
                      referencePropertySetId,
                      user ? user.language : undefined
                  ]
              ),
              valueLabel : string                                                         = useMemo(
                  () => {
                      let retVal : string = '';

                      if ( propertySetId ) {
                          retVal = getPropertyValueDisplayName( propertySetId, schema );
                      }
                      else if ( referencePropertySetId ) {
                          retVal = getReferencePropertyValueDisplayName( referencePropertySetId, schema );
                      }

                      return retVal;
                  },
                  [
                      schema,
                      propertySetId,
                      referencePropertySetId,
                      user ? user.language : undefined
                  ]
              ),
              errors : EnumValueErrors                                                    = useMemo(
                  () => {
                      let retVal = [];

                      if ( errorMessage && typeof errorMessage === 'string' ) {
                          try {
                              retVal = JSON.parse( errorMessage );
                          }
                              // eslint-disable-next-line no-empty
                          catch ( e ) {
                          }
                      }

                      return retVal;
                  },
                  [
                      errorMessage
                  ]
              ),
              // The conditions of keyStyle/valueStyle/rowStyle are about a single entry, so they cannot be
              // resolved once for the whole field ( as the Form does with style and labelStyle ). The
              // alias of the field addresses the value of an entry, the one the Form passes as
              // keyDataDescriptorAlias its key; both are overwritten per entry. Every other alias of
              // the form stays readable - a condition may well mix the entry with a value from
              // elsewhere in the form.
              getEntryStyleClassNames                                                     = useCallback(
                  ( styles : Style[], entry : Pick<TransformedValue, 'key' | 'value'>, styleSelectors? : string[] ) : string[] => {
                      let retVal : string[] = [];

                      if ( styles.length && dataDescriptorAlias ) {
                          const values = {
                              ...valuesForEntryStyle,
                              [ dataDescriptorAlias ]: entry.value
                          } as DataDescriptorValues;

                          if ( keyDataDescriptorAlias ) {
                              values[ keyDataDescriptorAlias ] = entry.key;
                          }

                          retVal = resolveStyleToClassNames( styles, values, undefined, styleSelectors );
                      }

                      return retVal;
                  },
                  [
                      dataDescriptorAlias,
                      keyDataDescriptorAlias,
                      JSON.stringify( valuesForEntryStyle )
                  ]
              ),
              getValueEditorParams : GetValueEditorParams                                 = useMemo(
                  () => {
                      return {
                          schema,
                          timezone,
                          language: user ? user.language : undefined
                      };
                  },
                  [
                      schema,
                      timezone,
                      user
                  ]
              ),
              heightStyle : React.CSSProperties                                           = useMemo(
                  () => {
                      if ( presentation !== 'chips' ) {
                          return getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout );
                      }
                      else {
                          return {};
                      }
                  },
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              messageResolver                                                             = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder                                                         = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              resolvedNewStatusId                                                         = useMemo(
                  () => {
                      return newStatusId ?? metaModel[ schema ].getStatus( 'active' )!.id;
                  },
                  [
                      newStatusId
                  ]
              ),
              resolvedDeleteStatusId                                                      = useMemo(
                  () => {
                      return deleteStatusId ?? metaModel[ schema ].getStatus( 'deleted' )!.id;
                  },
                  [
                      deleteStatusId
                  ]
              ),
              deletedStatusIds                                                            = useMemo(
                  () => metaModel[ schema ].getDeletedStatus().map( s => s.id ),
                  [
                      schema
                  ]
              ),
              updateKeyToUniqueIdMapping                                                  = useCallback(
                  ( tValue : TransformedValue[] ) => {
                      dispatch( setElusiveData( elementId, {
                          keyToUniqueIdMapping: tValue.reduce(
                              ( mapping, v ) => {
                                  mapping[ v.originalStringKey ] = v.uniqueId;

                                  return mapping;
                              },
                              {} as FieldElusiveData[ 'keyToUniqueIdMapping' ] )
                      } ) );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              getNewUniqueId                                                              = useCallback(
                  ( uniqueCount : number, tValue? : TransformedValue[] ) => {
                      let retVal : string | undefined,
                          count = uniqueCount;

                      tValue = tValue ?? currentTransformedValue.current;

                      do {
                          retVal = `nId_${count++}`;
                      } while ( tValue.find( tV => tV.uniqueId === retVal ) )

                      return retVal;
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              getEmptyEntry                                                               = useCallback(
                  ( uniqueId : string, entryIndex : number ) => {
                      return {
                          uniqueId         : uniqueId,
                          originalStringKey: `#NEW#${entryIndex}#`,
                          id               : 0,
                          key              : '',
                          value            : '',
                          orderNum         : 0,
                          statusId         : resolvedNewStatusId
                      };
                  },
                  [
                      resolvedNewStatusId
                  ]
              ),
              transformedValue                                                            = useMemo(
                  () => {
                      const retVal : TransformedValue[] = [],
                            sortFnc                     = ( value1 : TransformedValue, value2 : TransformedValue, orderBy : OrderBy ) : -1 | 0 | 1 => {
                                let orderValue1 = value1[ orderBy.key ],
                                    orderValue2 = value2[ orderBy.key ];

                                // hold new entries sorted at the bottom
                                //if ( value1.id < 1 && value2.id < 1 ) {
                                if ( value1.id === 0 && value2.id === 0 ) {
                                    if ( value1.uniqueId > value2.uniqueId ) {
                                        return orderBy.direction === 'asc' ? 1 : -1;
                                    }
                                    else if ( value1.uniqueId < value2.uniqueId ) {
                                        return orderBy.direction === 'asc' ? -1 : 1;
                                    }
                                    else {
                                        return 0;
                                    }
                                }
                                //else if ( value1.id < 1 ) {
                                else if ( value1.id === 0 ) {
                                    return orderBy.direction === 'asc' ? 1 : -1;
                                }
                                //else if ( value2.id < 1 ) {
                                else if ( value2.id === 0 ) {
                                    return orderBy.direction === 'asc' ? -1 : 1;
                                }

                                // order strings case independent
                                if ( orderValue1 && typeof orderValue1 === 'string' ) {
                                    orderValue1 = orderValue1.toLowerCase();
                                }
                                if ( orderValue2 && typeof orderValue2 === 'string' ) {
                                    orderValue2 = orderValue2.toLowerCase();
                                }

                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                if ( orderValue1 > orderValue2 ) {
                                    return orderBy.direction === 'asc' ? 1 : -1;
                                }
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                else if ( orderValue1 < orderValue2 ) {
                                    return orderBy.direction === 'asc' ? -1 : 1;
                                }
                                else {
                                    return 0;
                                }
                            };

                      let newIdMapping : NewIdMapping = {};

                      if ( value ) {
                          newIdMapping = Object.entries( value )
                              .filter( // limit to new entries
                                  ( [ k, v ] ) => v.id <= 0
                              )
                              .sort( // sort by id desc
                                  ( [ k1, v1 ], [ k2, v2 ] ) => {
                                      if ( v1.id > v2.id ) {
                                          return -1;
                                      }
                                      else if ( v1.id < v2.id ) {
                                          return 1;
                                      }
                                      else {
                                          return 0;
                                      }
                                  }
                              )
                              .reduce(
                                  ( mapping, [ k, v ] ) => {

                                      // get old uniqueId from keyToUniqueIdMapping
                                      if ( elusiveData?.keyToUniqueIdMapping[ k ] ) {
                                          mapping[ k ] = elusiveData?.keyToUniqueIdMapping[ k ];
                                      }
                                      else {
                                          const valueKeys               = Object.keys( value ),
                                                usedUniqueIds           = Object.values( mapping ),
                                                oldUniqueIds            = elusiveData?.keyToUniqueIdMapping ? Object.values( elusiveData?.keyToUniqueIdMapping ) : [],
                                                oldUniqueIdToKeyMapping = Object.entries( elusiveData?.keyToUniqueIdMapping ?? {} ).reduce(
                                                    ( innerMapping, [ oldKey, oldUniqueId ] ) => {
                                                        innerMapping[ oldUniqueId ] = oldKey;
                                                        return innerMapping;
                                                    },
                                                    {} as { [ uniqueId : string ] : string }
                                                );

                                          let count = 1,
                                              uniqueId : string,
                                              oldKey : string;

                                          do {
                                              uniqueId = getNewUniqueId( count++, [] );
                                              oldKey = oldUniqueIdToKeyMapping[ uniqueId ];
                                          } while ( usedUniqueIds.includes( uniqueId ) || ( oldUniqueIds.includes( uniqueId ) && valueKeys.includes( oldKey ) ) )

                                          if ( uniqueId ) {
                                              mapping[ k ] = uniqueId;
                                          }
                                      }

                                      return mapping;
                                  },
                                  {} as { [ stringKey : string ] : string }
                              );

                          let orderBy : OrderBy[] = [
                              { key: 'key', direction: 'asc' },
                              { key: 'value', direction: 'asc' },
                              { key: 'orderNum', direction: 'asc' }
                          ];

                          for ( const key of Object.keys( value ) as string[] ) {
                              let uniqueId : string;

                              if ( value[ key ].id > 0 ) {
                                  uniqueId = String( value[ key ].id );
                              }
                              else {
                                  uniqueId = newIdMapping[ key ];
                              }

                              retVal.push( {
                                  uniqueId,
                                  originalStringKey: key,
                                  ...value[ key ]
                              } );
                          }

                          switch ( listingOrder ) {
                              case 'order':
                                  orderBy = [
                                      { key: 'orderNum', direction: 'asc' },
                                      { key: 'key', direction: 'asc' },
                                      { key: 'value', direction: 'asc' }
                                  ];
                                  break;

                              case 'asc':
                                  if ( propertySet && !propertySet.isKeySettable ) {
                                      orderBy = [
                                          { key: 'value', direction: 'asc' },
                                          { key: 'key', direction: 'asc' },
                                          { key: 'orderNum', direction: 'asc' }
                                      ];
                                  }
                                  // else -> default
                                  break;

                              case 'desc':
                                  if ( propertySet && !propertySet.isKeySettable ) {
                                      orderBy = [
                                          { key: 'value', direction: 'desc' },
                                          { key: 'key', direction: 'asc' },
                                          { key: 'orderNum', direction: 'asc' }
                                      ];
                                  }
                                  else {
                                      orderBy = [
                                          { key: 'key', direction: 'desc' },
                                          { key: 'value', direction: 'asc' },
                                          { key: 'orderNum', direction: 'asc' }
                                      ];
                                  }
                                  break;

                              case 'keyAscValueAsc':
                                  // -> default
                                  break;

                              case 'keyAscValueDesc':
                                  orderBy = [
                                      { key: 'key', direction: 'asc' },
                                      { key: 'value', direction: 'desc' },
                                      { key: 'orderNum', direction: 'asc' }
                                  ];
                                  break;

                              case 'keyDescValueAsc':
                                  orderBy = [
                                      { key: 'key', direction: 'desc' },
                                      { key: 'value', direction: 'asc' },
                                      { key: 'orderNum', direction: 'asc' }
                                  ];
                                  break;

                              case 'keyDescValueDesc':
                                  orderBy = [
                                      { key: 'key', direction: 'desc' },
                                      { key: 'value', direction: 'desc' },
                                      { key: 'orderNum', direction: 'asc' }
                                  ];
                                  break;

                              case 'valueAscKeyAsc':
                                  orderBy = [
                                      { key: 'value', direction: 'asc' },
                                      { key: 'key', direction: 'asc' },
                                      { key: 'orderNum', direction: 'asc' }
                                  ];
                                  break;

                              case 'valueAscKeyDesc':
                                  orderBy = [
                                      { key: 'value', direction: 'asc' },
                                      { key: 'key', direction: 'desc' },
                                      { key: 'orderNum', direction: 'asc' }
                                  ];
                                  break;

                              case 'valueDescKeyAsc':
                                  orderBy = [
                                      { key: 'value', direction: 'desc' },
                                      { key: 'key', direction: 'asc' },
                                      { key: 'orderNum', direction: 'asc' }
                                  ];
                                  break;

                              case 'valueDescKeyDesc':
                                  orderBy = [
                                      { key: 'value', direction: 'desc' },
                                      { key: 'key', direction: 'desc' },
                                      { key: 'orderNum', direction: 'asc' }
                                  ];
                                  break;
                          }

                          retVal.sort( ( value1, value2 ) => {
                              let innerRetVal : number,
                                  orderByCount : number = 0;

                              do {
                                  innerRetVal = sortFnc( value1, value2, orderBy[ orderByCount++ ] );
                              } while ( innerRetVal === 0 && orderByCount < 3 )

                              return innerRetVal;
                          } );
                      }

                      if (
                          !disabled
                          && extensible
                          && (
                              addNewEntry
                              || (
                                  presentation === 'fieldList'
                                  || presentation === 'fieldList_reversed'
                              )
                          )
                          && !retVal.find( v => v.id === 0 && ( !v.key || !v.value ) )
                      ) {
                          retVal.push(
                              getEmptyEntry(
                                  getNewUniqueId( retVal.filter( v => v.id < 1 ).length + 1, retVal ),
                                  retVal.length + 1
                              )
                          );
                      }

                      return retVal;
                  },
                  [
                      JSON.stringify( value ), // check if value really changed
                      extensible,
                      disabled,
                      presentation,
                      listingOrder,
                      propertySet,
                      addNewEntry
                  ]
              ),
              [ elementValue, setElementValue ]                                           = useState<TransformedValue[]>( transformedValue ),
              currentElementValue                                                         = useRef<TransformedValue[]>( transformedValue ),
              currentTransformedValue                                                     = useRef<TransformedValue[]>( transformedValue ),
              buildUpdateAndSetElementValue                                               = useCallback(
                  <K extends keyof EnumUpdateValueAttributes>( originalStringKey : string, attribute : K, attributeValue : EnumUpdateValueAttributes[ K ], append : boolean = false ) : EnumUpdateValue | undefined => {
                      const newElementValue = [ ...currentElementValue.current ];

                      // append new entry if
                      if ( append ) {
                          const entryToAppend = getEmptyEntry(
                              getNewUniqueId( newElementValue.filter( v => v.id < 1 ).length + 1 ),
                              newElementValue.length + 1
                          );

                          newElementValue.push( entryToAppend );
                          originalStringKey = entryToAppend.originalStringKey;
                      }

                      const elementValueForAttributeIndex = newElementValue.findIndex( nEV => nEV.originalStringKey === originalStringKey );

                      let retVal : EnumUpdateValue | undefined = {};

                      if ( elementValueForAttributeIndex !== -1 && newElementValue[ elementValueForAttributeIndex ][ attribute ] !== attributeValue ) {
                          newElementValue[ elementValueForAttributeIndex ] = {
                              ...newElementValue[ elementValueForAttributeIndex ],
                              [ attribute ]: attributeValue ?? ''
                          };

                          // reordering
                          if ( attribute === 'orderNum' && attributeValue ) {
                              let enumProperty : EnumProperty | EnumReferenceProperty | undefined;

                              if ( propertySetId ) {
                                  const pSet = currentPropertySet.current as PropertySet,
                                        item = new Item( { itemTypeId: pSet.itemTypeId }, undefined, pSet.schema, true );

                                  enumProperty = new EnumProperty( item, {
                                      itemId       : item.id,
                                      propertySetId: pSet.id,
                                      // add existing properties
                                      properties: ( currentElementValue.current
                                          .filter( cEV => cEV.id > 0 )
                                          .map( cEV => {
                                              return {
                                                  itemId       : item.id,
                                                  propertySetId: pSet.id,
                                                  ..._.pick( cEV, [ 'id', 'key', 'value', 'statusId', 'orderNum' ] )
                                              }
                                          } ) as EnumPropertyAttributes[ 'properties' ] )
                                  } );
                              }
                              else {
                                  const rpSet     = currentPropertySet.current as ReferencePropertySet,
                                        reference = new Reference( { referenceSetId: rpSet.referenceSetId, srcItemId: -1, destItemId: -2 }, undefined, rpSet.schema, true );

                                  enumProperty = new EnumReferenceProperty( reference, {
                                      referenceId           : reference.id,
                                      referencePropertySetId: rpSet.id,
                                      // add existing properties
                                      properties: ( currentElementValue.current
                                          .filter( cEV => cEV.id > 0 )
                                          .map( cEV => {
                                              return {
                                                  referenceId           : reference.id,
                                                  referencePropertySetId: rpSet.id,
                                                  ..._.pick( cEV, [ 'id', 'key', 'value', 'statusId', 'orderNum' ] )
                                              }
                                          } ) as EnumReferencePropertyAttributes[ 'properties' ] )
                                  } );
                              }

                              // add new properties
                              const newPropertyIdMapping : { [ newId : number ] : number } = {};
                              currentElementValue.current
                                  .filter( cEV => cEV.id < 0 )
                                  .forEach( cEV => {
                                      if ( cEV.key ) {
                                          const property = enumProperty?.addProperty( cEV.key );

                                          if ( property ) {
                                              property.update( { ..._.pick( cEV, [ 'id', 'key', 'value', 'statusId', 'orderNum' ] ) } );

                                              newPropertyIdMapping[ property.id ] = cEV.id;
                                          }
                                      }
                                  } );

                              if ( enumProperty?.properties.has( originalStringKey ) ) {
                                  enumProperty.properties.get( originalStringKey )!.orderNum = attributeValue as number;

                                  enumProperty.properties.forEach( ( property ) => {
                                      if ( property.isAttributeModified( 'orderNum' ) ) {
                                          const index = newElementValue.findIndex( cV => cV.id === property.id || cV.id === newPropertyIdMapping[ property.id ] );

                                          if ( index !== -1 ) {
                                              newElementValue[ index ] = {
                                                  ...newElementValue[ index ],
                                                  orderNum: property.orderNum
                                              };
                                          }
                                      }
                                  } );
                              }
                          }

                          // append new entry if
                          if (
                              !disabled
                              && extensible
                              && (
                                  addNewEntry
                                  || (
                                      presentation === 'fieldList'
                                      || presentation === 'fieldList_reversed'
                                  )
                              )
                              && !newElementValue.find( v => v.id === 0 && ( v.key === '' || v.value === '' ) )
                          ) {
                              newElementValue.push(
                                  getEmptyEntry(
                                      getNewUniqueId( newElementValue.filter( v => v.id < 1 ).length + 1 ),
                                      newElementValue.length + 1
                                  )
                              );
                          }

                          setElementValue( newElementValue );
                          updateKeyToUniqueIdMapping( newElementValue );

                          // get update value
                          for ( const currentValue of newElementValue ) {
                              // new -> set all relevant attributes
                              if ( currentValue.id === 0 && currentValue.key ) {
                                  retVal[ currentValue.originalStringKey ] = {
                                      ..._.omit( currentValue, 'originalStringKey', 'uniqueId' )
                                  };
                              }

                              // normal update
                              else {
                                  const origValue = currentTransformedValue.current.find( cTV => cTV.originalStringKey === currentValue.originalStringKey );

                                  if ( currentValue !== origValue ) {
                                      const changes = ( origValue ? objectUtils.getDiffKeys( _.pick( origValue, enumUpdateValueAttributes ), _.pick( currentValue, enumUpdateValueAttributes ) ) : [] ) as ( keyof EnumUpdateValueAttributes )[];

                                      if ( changes.length ) {
                                          retVal[ currentValue.originalStringKey ] = {
                                              id: currentValue.id
                                          };

                                          for ( const changedAttribute of changes ) {
                                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                              // @ts-ignore
                                              retVal[ currentValue.originalStringKey ][ changedAttribute ] = currentValue[ changedAttribute ];
                                          }
                                      }
                                  }
                              }
                          }
                      }

                      if ( !Object.keys( retVal ).length ) {
                          retVal = undefined;
                      }

                      return retVal;
                  },
                  [
                      addNewEntry,
                      disabled,
                      extensible,
                      getEmptyEntry,
                      getNewUniqueId,
                      presentation,
                      updateKeyToUniqueIdMapping
                  ]
              ),
              onDeleteButtonClick                                                         = useCallback(
                  ( originalStringKey : string, originalEvent : any ) => {
                      const newValue = buildUpdateAndSetElementValue(
                          originalStringKey,
                          'statusId',
                          resolvedDeleteStatusId
                      );

                      if ( newValue ) {
                          if ( !useHookForm ) {
                              onChange( newValue, originalEvent );
                          }
                          else {
                              onIntermediateChange?.( newValue, originalEvent );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, newValue, originalEvent ), useHookFormDelay );
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      onChange,
                      onIntermediateChange,
                      resolvedDeleteStatusId,
                      useHookForm
                  ]
              ),
              onInputChange                                                               = useCallback(
                  <K extends keyof EnumUpdateValueAttributes>( originalStringKey : string, attribute : K, attributeValue : EnumUpdateValueAttributes[ K ] | undefined, originalEvent : any ) => {
                      const newValue = buildUpdateAndSetElementValue(
                          originalStringKey,
                          attribute,
                          attributeValue
                      );

                      if ( newValue ) {
                          if ( !useHookForm ) {
                              onChange( newValue, originalEvent );
                          }
                          else {
                              onIntermediateChange?.( newValue, originalEvent );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, newValue, originalEvent ), useHookFormDelay );
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              onSorterMouseDown                                                           = useCallback(
                  ( originalStringKey : string, originalEvent : React.MouseEvent ) => {
                      const nativeEvent = originalEvent.nativeEvent;

                      let sorterElement : HTMLElement = originalEvent.target as HTMLElement,
                          sorterElements : NodeListOf<Element>,
                          sorterElementBox : DOMRect;

                      while ( sorterElement && !sorterElement?.classList.contains( 'yn-enumfield-entry' ) ) {
                          sorterElement = sorterElement.parentNode as HTMLElement;
                      }

                      if ( sorterElement ) {
                          sorterElements = ( sorterElement?.parentNode?.querySelectorAll( '.yn-enumfield-entry' ) ?? [] ) as NodeListOf<Element>;
                          sorterElementBox = sorterElement.getBoundingClientRect();

                          let sorterIndex = 0;
                          for ( let i = 0; i < sorterElements.length; i++ ) {
                              if ( sorterElement === sorterElements[ i ] ) {
                                  sorterIndex = i;
                                  break;
                              }
                          }

                          // create drag clone
                          dragElement.current = document.createElement( 'div' ) as unknown as EnumDragElement;
                          dragElement.current.appendChild( sorterElement.cloneNode( true ) );
                          dragElement.current.className = sorterElement.parentElement?.className ?? '';
                          dragElement.current.classList.add( 'yn-drag-item' );

                          dragElement.current.id = `${sorterElement.id}_clone`;
                          dragElement.current.sorterElement = sorterElement;
                          dragElement.current.sorterIndex = sorterIndex;
                          dragElement.current.originalSorterIndex = sorterIndex;
                          dragElement.current.offsetX = nativeEvent.pageX - sorterElementBox.x;
                          dragElement.current.offsetY = nativeEvent.pageY - sorterElementBox.y;
                          dragElement.current.originalStringKey = originalStringKey;

                          dragElement.current.style.width = `calc( ${sorterElement.clientWidth}px + .5rem )`;
                          dragElement.current.style.left = `calc( ${nativeEvent.pageX - dragElement.current.offsetX}px - .25rem )`;
                          dragElement.current.style.top = `${nativeEvent.pageY - dragElement.current.offsetY}px`;

                          // set original to invisible
                          dragElement.current.sorterElement.classList.add( 'yn-drag-placeholder' );

                          document.body.appendChild( dragElement.current );

                          // create global mouse move handler
                          document.querySelector( 'html' )?.addEventListener( 'mousemove', onSorterDragMove );

                          // create global mouseup handler
                          document.querySelector( 'html' )?.addEventListener( 'mouseup', onSorterDragEnd );

                          nativeEvent.preventDefault();
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onSorterKeyDown                                                             = useCallback(
                  ( originalStringKey : string, originalEvent : React.KeyboardEvent ) => {
                      const elementValueForSorter = currentElementValue.current.find( tV => tV.originalStringKey === originalStringKey ),
                            orderNum              = elementValueForSorter ? elementValueForSorter.orderNum : undefined;

                      let newOrderNum : number = 0;

                      if ( orderNum !== undefined ) {
                          if ( originalEvent.key === 'ArrowUp' ) {
                              if ( orderNum > 1 ) {
                                  newOrderNum = orderNum - 1;
                              }
                              originalEvent.preventDefault();
                              originalEvent.stopPropagation();
                          }
                          else if ( originalEvent.key === 'ArrowDown' ) {
                              if ( orderNum < currentElementValue.current.length ) {
                                  newOrderNum = orderNum + 1;
                              }
                              originalEvent.preventDefault();
                              originalEvent.stopPropagation();
                          }
                      }

                      if ( newOrderNum ) {
                          const newValue = buildUpdateAndSetElementValue(
                              originalStringKey,
                              'orderNum',
                              newOrderNum
                          );

                          if ( newValue ) {
                              if ( !useHookForm ) {
                                  onChange( newValue, originalEvent );
                              }
                              else {
                                  onIntermediateChange?.( newValue, originalEvent );

                                  if ( timeoutId.current ) {
                                      clearTimeout( timeoutId.current );
                                  }
                                  timeoutId.current = setTimeout( onChange.bind( null, newValue, originalEvent ), useHookFormDelay );
                              }
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              onSorterDragMove                                                            = useCallback(
                  ( event : MouseEvent ) => {
                      if ( dragElement.current ) {
                          const parentElement    = dragElement.current.sorterElement.parentNode as HTMLElement,
                                parentElementBox = parentElement?.getBoundingClientRect(),
                                sorterElements   = parentElement?.querySelectorAll( '.yn-enumfield-entry' ),
                                oldY             = parseInt( dragElement.current.style.top ),
                                y                = Math.round( event.pageY - dragElement.current.offsetY );

                          // set position
                          dragElement.current.style.top = y + 'px';

                          // position within parent
                          if (
                              parentElementBox.y < event.pageY && event.pageY < parentElementBox.y + parentElementBox.height
                          ) {

                              // move up ?
                              if ( y < oldY ) {

                                  // has to change position with a sorter before?
                                  for ( let sorterIndex = 0; sorterIndex < dragElement.current.sorterIndex; sorterIndex++ ) {
                                      const sorterElementBox        = sorterElements[ sorterIndex ].getBoundingClientRect(),
                                            sorterElementClassNames = sorterElements[ sorterIndex ].className.split( ' ' );

                                      if ( y < sorterElementBox.y + ( sorterElementBox.height / 2 ) && !sorterElementClassNames.includes( 'yn-enumfield-entry-label' ) ) {
                                          parentElement.removeChild( dragElement.current.sorterElement );

                                          parentElement.insertBefore( dragElement.current.sorterElement, sorterElements[ sorterIndex ] );

                                          dragElement.current.sorterIndex = sorterIndex;

                                          break;
                                      }
                                  }
                              }

                              // move down?
                              else if ( y > oldY ) {

                                  // has to change position with a sorter after?
                                  for ( let sorterIndex = sorterElements.length - 1; sorterIndex > dragElement.current.sorterIndex; sorterIndex-- ) {
                                      const sorterElementBox        = sorterElements[ sorterIndex ].getBoundingClientRect(),
                                            sorterElementClassNames = sorterElements[ sorterIndex ].className.split( ' ' );

                                      if ( y > sorterElementBox.y - sorterElementBox.height && !sorterElementClassNames.includes( 'yn-enumfield-entry-new' ) ) {
                                          parentElement.removeChild( dragElement.current.sorterElement );

                                          if ( sorterIndex < sorterElements.length - 1 ) {
                                              parentElement.insertBefore( dragElement.current.sorterElement, sorterElements[ sorterIndex ].nextSibling );
                                          }
                                          else {
                                              parentElement.appendChild( dragElement.current.sorterElement );
                                          }

                                          dragElement.current.sorterIndex = sorterIndex;

                                          break;
                                      }
                                  }
                              }
                          }

                          event.preventDefault();
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onSorterDragEnd                                                             = useCallback(
                  ( inEvent : MouseEvent | React.MouseEvent ) => {
                      if ( dragElement.current ) {
                          // switch to real event
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          const event            = inEvent.nativeEvent ?? inEvent,
                                parentElement    = dragElement.current.sorterElement.parentNode as HTMLElement,
                                parentElementBox = parentElement.getBoundingClientRect(),
                                sorterElements   = parentElement.querySelectorAll( '.yn-sorter' );

                          // cleanup global mouse move handler
                          document.querySelector( 'html' )?.removeEventListener( 'mousemove', onSorterDragMove );

                          // cleanup global mouseup handler
                          document.querySelector( 'html' )?.removeEventListener( 'mouseup', onSorterDragEnd );

                          // place at original position if dropped outside parent and not there
                          if (
                              (
                                  event.pageX < parentElementBox.x
                                  || event.pageX > parentElementBox.x + parentElementBox.width
                                  || event.pageY < parentElementBox.y
                                  || event.pageY > parentElementBox.y + parentElementBox.height
                              )
                              && sorterElements[ dragElement.current.originalSorterIndex ] !== dragElement.current.sorterElement
                          ) {
                              parentElement.removeChild( dragElement.current.sorterElement );

                              if ( dragElement.current.originalSorterIndex < sorterElements.length - 1 ) {
                                  parentElement.insertBefore( dragElement.current.sorterElement, sorterElements[ dragElement.current.originalSorterIndex ] );
                              }
                              else {
                                  parentElement.appendChild( dragElement.current.sorterElement );
                              }
                          }

                          // reorder sorters
                          if ( dragElement.current.originalSorterIndex !== dragElement.current.sorterIndex ) {
                              const newValue = buildUpdateAndSetElementValue(
                                  dragElement.current.originalStringKey,
                                  'orderNum',
                                  dragElement.current.sorterIndex
                              );

                              if ( newValue ) {
                                  if ( !useHookForm ) {
                                      onChange( newValue, event );
                                  }
                                  else {
                                      onIntermediateChange?.( newValue, event );

                                      if ( timeoutId.current ) {
                                          clearTimeout( timeoutId.current );
                                      }
                                      timeoutId.current = setTimeout( onChange.bind( null, newValue, event ), useHookFormDelay );
                                  }
                              }
                          }

                          // set original to visible
                          if ( dragElement.current.sorterElement ) {
                              dragElement.current.sorterElement.classList.remove( 'yn-drag-placeholder' );
                          }

                          // cleanup dragElement
                          if ( dragElement ) {
                              document.body.removeChild( dragElement.current );
                              dragElement.current = undefined;
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              cellEditor                                                                  = useCallback(
                  ( options : ColumnEditorOptions ) : React.ReactNode => {
                      const elementValueForRow : TransformedValue = options.rowData,
                            columnId                              = options.field.replace( /Body$/, '' ) as ColumnId,
                            rowId                                 = `row_${elementId}_${elementValueForRow.uniqueId}`,
                            cellId                                = `${rowId}_${columnId}`;

                      return currentColumnsCache.current?.[ columnId ]?.cellCache[ cellId ]?.editor ?? currentColumnsCache.current?.[ columnId ]?.cellCache[ cellId ]?.body;
                  },
                  [
                      elementId
                  ]
              ),
              onResolveRowClass                                                           = useCallback(
                  ( elementValueForRow : TransformedValue ) => {
                      const retVal : {
                                [ className : string ] : boolean
                            }                     = {
                                'yn-sorter-error'   : false,
                                'yn-sorter-dirty'   : false,
                                'yn-sorter-hidden'  : false,
                                'yn-sorter-disabled': false
                            },
                            originalValueForRow = originalValue
                                ? Object.values( originalValue ).find( v => v.id === elementValueForRow.id )
                                : undefined;

                      if ( errors[ elementValueForRow.originalStringKey ]?.[ 'orderNum' ] ) {
                          retVal[ 'yn-sorter-error' ] = true;
                      }

                      if ( markDirty && originalValueForRow !== undefined && elementValueForRow.orderNum !== originalValueForRow?.orderNum ) {
                          retVal[ 'yn-sorter-dirty' ] = true;
                      }

                      if ( elementValueForRow.id === 0 ) {
                          retVal[ 'yn-sorter-hidden' ] = true;
                      }

                      if ( disabled ) {
                          retVal[ 'yn-sorter-disabled' ] = true;
                      }

                      // rowStyle - the row of a grid is drawn by the DataTable, which takes its classes
                      // from here
                      for ( const className of getEntryStyleClassNames( rowStyle, elementValueForRow, enumEntryRowStyleSelectors ) ) {
                          retVal[ className ] = true;
                      }

                      return retVal;
                  },
                  [
                      disabled,
                      errors,
                      getEntryStyleClassNames,
                      originalValue,
                      markDirty,
                      rowStyle
                  ]
              ),
              onAddRowButtonClick                                                         = useCallback(
                  () => {
                      if ( !currentElementValue.current.find( v => v.id === 0 ) ) {
                          setAddNewEntry( true );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onRowReorder                                                                = useCallback(
                  ( event : DataTableRowReorderEvent<RowData[]> ) => {
                      const newValue = buildUpdateAndSetElementValue(
                          currentElementValue.current[ event.dragIndex ].originalStringKey,
                          'orderNum',
                          event.dropIndex + 1
                      );

                      if ( newValue ) {
                          if ( !useHookForm ) {
                              onChange( newValue, event.originalEvent );
                          }
                          else {
                              onIntermediateChange?.( newValue, event.originalEvent );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, newValue, event.originalEvent ), useHookFormDelay );
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      onChange,
                      onIntermediateChange,
                      useHookForm
                  ]
              ),
              chipsItemTemplate                                                           = useCallback(
                  ( elementValueForChip : TransformedValue ) : React.ReactNode => {
                      const chipId = `chip_${elementId}_${elementValueForChip.uniqueId}`,
                            cache  = currentChipsCache.current[ chipId ];

                      let retVal : React.ReactNode | undefined;

                      if ( cache ) {
                          retVal = cache.item;

                          if ( cache.errorMessage ) {
                              retVal = <>
                                  {retVal}
                                  <Tooltip
                                      target={`#${chipId}`}
                                      position="top"
                                      className="yn-tooltip"
                                  >
                                      <div className="yn-tooltip-error">
                                          <div className="yn-tooltip-header">
                                              {__( 'YN.base.common.tooltip.error.title' )}
                                          </div>
                                          {cache.errorMessage}
                                      </div>
                                  </Tooltip>
                              </>;
                          }
                      }

                      return retVal;
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onChipsAdd                                                                  = useCallback(
                  ( event : ChipsAddEvent ) => {
                      if ( !Object.keys( errors ).length ) {
                          const originalStringKey = `#NEW#${currentElementValue.current.length + 1}#`,
                                newValue          = buildUpdateAndSetElementValue(
                                    originalStringKey,
                                    currentPropertySet.current?.isKeySettable ? 'key' : 'value',
                                    event.value as string,
                                    true
                                );

                          if ( newValue ) {
                              if ( !useHookForm ) {
                                  onChange( newValue, event.originalEvent );
                              }
                              else {
                                  onIntermediateChange?.( newValue, event.originalEvent );

                                  if ( timeoutId.current ) {
                                      clearTimeout( timeoutId.current );
                                  }
                                  timeoutId.current = setTimeout( onChange.bind( null, newValue, event.originalEvent ), useHookFormDelay );
                              }
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      errors,
                      onChange,
                      onIntermediateChange,
                      resolvedNewStatusId,
                      useHookForm
                  ]
              ),
              onChipsRemove                                                               = useCallback(
                  ( event : ChipsRemoveEvent ) => {
                      const newValue = buildUpdateAndSetElementValue(
                          ( event.value as unknown as TransformedValue[] )[ 0 ][ 'originalStringKey' ],
                          'statusId',
                          resolvedDeleteStatusId
                      );

                      if ( newValue ) {
                          if ( !useHookForm ) {
                              onChange( newValue, event.originalEvent );
                          }
                          else {
                              onIntermediateChange?.( newValue, event.originalEvent );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, newValue, event.originalEvent ), useHookFormDelay );
                          }
                      }
                  },
                  [
                      buildUpdateAndSetElementValue,
                      onChange,
                      onIntermediateChange,
                      resolvedDeleteStatusId,
                      useHookForm
                  ]
              ),
              onChipsKeyDown                                                              = useCallback(
                  ( event : React.KeyboardEvent<HTMLInputElement> ) => {
                      const chipsInput     = chipsComponentRef.current?.getInput(),
                            messageElement = document.getElementById( `${elementId}_message` ),
                            currentValues  = currentElementValue.current.map( tV => tV.originalStringKey );

                      let inputValue = event.currentTarget.value;

                      // get real input value (handle event.key)
                      // backspace key -> remove last char from inputValue
                      if ( event.key === 'Backspace' ) {
                          inputValue = inputValue.substring( 0, inputValue.length - 1 );
                      }
                      // normal key -> add to inputValue
                      else if ( event.key.length === 1 ) {
                          inputValue += event.key;
                      }

                      // invalid
                      if ( currentValues.includes( inputValue ) ) {
                          chipsInput?.classList.add( 'yn-error' );

                          if ( messageElement ) {
                              messageElement.classList.add( 'yn-error' );
                              messageElement.innerHTML = __( 'YN.base.form.enumField.error.valueNotUnique' );
                          }

                          // enter key -> avoid adding invalid
                          if ( event.key === 'Enter' ) {
                              event.defaultPrevented = true;
                          }
                      }
                      else {
                          chipsInput?.classList.remove( 'yn-error' );

                          if ( messageElement ) {
                              messageElement.classList.remove( 'yn-error' );
                              messageElement.innerHTML = '';
                          }
                      }
                  },
                  [
                      elementId
                  ]
              ),
              onChipsWrapperClick                                                         = useCallback(
                  ( event : React.MouseEvent<HTMLUListElement> ) => {
                      if ( window.getSelection()?.toString() !== '' ) {
                          event.preventDefault();
                          event.stopPropagation();
                      }
                  },
                  []
              ),
              onKeyFieldKeyDown                                                           = useCallback(
                  ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                      // the value field of a new entry stays disabled until the key is set and a disabled field
                      // is skipped by the browsers tab order -> tabbing out of the key leaves the entry.
                      // the tab commits the key ( editors firing onChange on blur only do so on the blur ),
                      // so the value field gets enabled right after -> move the focus into it.
                      // if it stays disabled ( key not accepted ) the browsers own tab handling is kept.
                      if (
                          event.key === 'Tab'
                          && !event.shiftKey
                          && !event.altKey
                          && !event.ctrlKey
                          && !event.metaKey
                      ) {
                          const keyInput      = event.target as HTMLInputElement | HTMLTextAreaElement,
                                entryElement  = ( event.currentTarget as HTMLElement ).closest( '.yn-enumfield-entry, tr' ),
                                // grid presentations render the cells of a row within a table row
                                isGridRow     = entryElement?.tagName === 'TR',
                                valueCellId   = keyInput.id ? keyInput.id.replace( /_key$/, '_value' ) : '',
                                getValueCell  = () => valueCellId ? document.getElementById( valueCellId )?.closest( 'td' ) ?? undefined : undefined,
                                getValueInput = () => entryElement?.querySelector( '.yn-enumfield-value input, .yn-enumfield-value textarea' ) as HTMLInputElement | HTMLTextAreaElement | null;

                          if (
                              ( keyInput.tagName === 'INPUT' || keyInput.tagName === 'TEXTAREA' )
                              && keyInput.value
                          ) {
                              // grid presentations show the value as plain content - its editor is created on
                              // the cell click only, so there is nothing the tab could put the focus into
                              if ( isGridRow && getValueCell() ) {
                                  // the editor replaces the cell content, so it carries the cells id itself
                                  const focusValueCellEditor = ( retries : number ) => {
                                      const valueElement = document.getElementById( valueCellId ),
                                            valueInput   = ( valueElement instanceof HTMLInputElement || valueElement instanceof HTMLTextAreaElement
                                                ? valueElement
                                                : valueElement?.querySelector( 'input, textarea' ) ) as HTMLElement | null | undefined;

                                      if ( valueInput ) {
                                          valueInput.focus();
                                      }
                                      else if ( retries ) {
                                          setTimeout( focusValueCellEditor.bind( null, retries - 1 ), 50 );
                                      }
                                  };

                                  event.preventDefault();

                                  // let the key cell switch back to its view mode first
                                  setTimeout(
                                      () => {
                                          // opens the cell editor
                                          getValueCell()?.click();

                                          focusValueCellEditor( 3 );
                                      },
                                      0
                                  );
                              }

                              // the value field of a new entry is still disabled -> it is skipped by the tab
                              else if ( getValueInput()?.disabled ) {
                                  const focusValueInput = ( retry : boolean ) => {
                                      const valueInput = getValueInput();

                                      if ( valueInput && !valueInput.disabled ) {
                                          if ( document.activeElement !== valueInput ) {
                                              valueInput.focus();
                                          }
                                      }
                                      else if ( retry ) {
                                          setTimeout( focusValueInput.bind( null, false ), 50 );
                                      }
                                  };

                                  setTimeout( focusValueInput.bind( null, true ), 0 );
                              }
                          }
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              getEditor                                                                   = useCallback(
                  ( attribute : 'key' | 'value', fieldId : string, elementValueForField : TransformedValue, fieldHasError : boolean, fieldIsDirty : boolean, fieldWidth : AllProps[ 'keyFieldWidth' ], fieldEditorParams : any | undefined, fieldTabIndex : number, additionalWrapperClassName? : string, entryStyleClassNames : string[] = [] ) => {
                      // the conditional style of the key resp. the value of this entry paints the input,
                      // so it rides with the other input class names
                      const fieldClassNames : string[] = [ 'yn-subfield', ...inputClassNames, ...entryStyleClassNames ],
                            classNames : string[]      = [];

                      let retVal : JSX.Element | undefined;

                      if ( currentPropertySet.current ) {
                          // error marking
                          if ( fieldHasError ) {
                              classNames.push( 'yn-error-border' );
                          }

                          // dirty marking
                          if ( fieldIsDirty
                              || (
                                  attribute === 'key'
                                  && elementValueForField.id === 0
                                  && elementValueForField.key !== undefined
                                  && elementValueForField.key !== null
                                  && elementValueForField.key !== ''
                              )
                          ) {
                              classNames.push( 'yn-dirty-border' );
                          }

                          // add row whose key is still missing -> the value cannot be edited yet
                          const isNewEntryWithoutKey = elementValueForField.id === 0
                              && currentPropertySet.current.isKeySettable
                              && !elementValueForField.key;

                          const editorParams = {
                              autoCompleteRequestParameters: {
                                  ...autoCompleteRequestParameters,
                                  attribute
                              },

                              ...fieldEditorParams,

                              // add row without a key yet -> keep it a single line
                              ...( attribute === 'value' && isNewEntryWithoutKey ? { height: 1 } : {} ),

                              classNames,
                              key                       : fieldId,
                              id                        : fieldId,
                              disabled                  : disabled || ( attribute === 'value' && currentPropertySet.current.isKeySettable && !elementValueForField.key ),
                              inputClassNames           : fieldClassNames,
                              inputStyle                : getStatusColorStyleObject( elementValueForField.statusId, schema ),
                              labelVisibility           : 'hidden',
                              messageClassNames         : 'hidden',
                              placeholder               : elementValueForField.id === 0
                                  ? (
                                      attribute === 'value' && isNewEntryWithoutKey
                                          ? __( 'YN.base.form.enumField.newEntry.valuePlaceholder' )
                                          : resolvedPlaceholder
                                  )
                                  : undefined,
                              useHookForm               : false,
                              appendToElementId         : 'document',
                              tabIndex                  : fieldTabIndex,
                              infoTextLayout            : 'text',
                              messageTemplateStringResolver,
                              displayValueWidthOffsetRem: -1
                          };

                          // max length for key
                          if ( attribute === 'key' ) {
                              editorParams.maxLength = 255 - currentPropertySet.current.getStringKey( { key: '', orderNum: 1 } ).length;
                          }

                          // valueSet editor
                          if ( attribute === 'value' && currentPropertySet.current.valueSetId ) {
                              retVal = getValueSetEditor(
                                  schema,
                                  currentPropertySet.current instanceof PropertySet ? 'property' : 'referenceProperty',
                                  currentPropertySet.current.id,
                                  elementValueForField.value ?? '',
                                  onInputChange.bind( null, elementValueForField.originalStringKey, 'value' ),
                                  false,
                                  false,
                                  {
                                      ...editorParams
                                  }
                              );
                          }

                          // valueType editor
                          else {
                              const fieldValueType = attribute === 'key' ? currentPropertySet.current.keyType : currentPropertySet.current.valueType;

                              if ( fieldEditorParams?.type && !fieldUtils.checkValueTypeForFieldType( fieldValueType, fieldEditorParams.type ) ) {
                                  retVal = <StaticContentField
                                      {..._.pick( getValueEditorParams, staticContentFieldComponentProps ) as StaticContentFieldComponentProps}

                                      label={undefined}
                                      labelVisibility="hidden"
                                      infoTextLayout="text"
                                      content={`${fieldTypeToItemTypeMapping[ fieldEditorParams.type as FormFieldProperties['type'] ]} is not allowed for data type ${fieldValueType} (allowed: ${fieldUtils.getAllowedFieldTypesForValueType( fieldValueType )?.join( ', ' )})`}
                                      inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                                      messageClassNames={[ 'hidden' ]}
                                      style={{ marginBottom: 0 }}
                                  />;
                              }
                              else {
                                  retVal = getValueEditor(
                                      fieldEditorParams?.type ?? fieldValueType,
                                      attribute === 'key' ? elementValueForField.key : elementValueForField.value,
                                      onInputChange.bind( null, elementValueForField.originalStringKey, attribute ),

                                      false,
                                      {
                                          ...getValueEditorParams,
                                          height   : false,
                                          valueUnit: currentPropertySet.current.valueUnit
                                      },
                                      {
                                          ...editorParams
                                      }
                                  );
                              }
                          }

                          if ( retVal ) {
                              const style : React.CSSProperties = {};

                              if ( fieldWidth !== '1' ) {
                                  if ( String( Number( fieldWidth ) ) === fieldWidth ) {
                                      style.flex = `${fieldWidth} ${fieldWidth} auto`;
                                  }
                                  else {
                                      style.flex = 'none';
                                      style.width = fieldWidth;
                                  }
                              }

                              retVal = <div
                                  className={`${wrapperClassName} ${additionalWrapperClassName} yn-enumfield-${attribute === 'key' ? 'key' : 'value'}`}
                                  style={style}
                                  onKeyDown={attribute === 'key' && !disabled ? onKeyFieldKeyDown : undefined}
                              >
                                  {retVal}
                              </div>;
                          }
                      }

                      return retVal;
                  },
                  [
                      appendToElementId,
                      autoCompleteRequestParameters,
                      disabled,
                      getValueEditorParams,
                      inputClassNames,
                      onKeyFieldKeyDown,
                      resolvedPlaceholder,
                      schema,
                      useHookForm,
                      wrapperClassName
                  ]
              ),
              valueToUseForCaching                                                        = useMemo(
                  () => {
                      return transformedValue !== currentTransformedValue.current ? transformedValue : elementValue;
                  },
                  [
                      transformedValue,
                      elementValue
                  ]
              ),
              fieldCache : FieldCache                                                     = useMemo(
                  () => {
                      if ( presentation === 'fieldList' || presentation === 'fieldList_reversed' ) {
                          const newFieldCache : FieldCache  = { ...currentFieldCache.current },
                                existingFieldIds : string[] = [];

                          let changed : boolean             = false,
                              fieldId : string | undefined,
                              labelStyle : React.CSSProperties | undefined,
                              cacheParameters : CacheParameters | undefined,
                              cacheParameterDiff : string[] = [];

                          // labels
                          if ( propertySet?.isKeySettable ) {
                              fieldId = `${elementId}_key_label`;
                              existingFieldIds.push( fieldId );

                              cacheParameters = {
                                  originalStringKey: 'keyLabel',
                                  statusId         : 0,
                                  fieldHasError    : false,
                                  fieldIsDirty     : false,
                                  inputClassNames,
                                  key              : 'keyLabel',
                                  value            : keyLabel,
                                  language         : user ? user.language : undefined,
                                  fieldWidth       : keyFieldWidth
                              };
                              cacheParameterDiff = objectUtils.getDiffKeys( newFieldCache[ fieldId ]?.cacheParameters ?? {}, cacheParameters );

                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  labelStyle = {};

                                  if ( keyFieldWidth !== '1' ) {
                                      if ( String( Number( keyFieldWidth ) ) === keyFieldWidth ) {
                                          labelStyle.flex = `${keyFieldWidth} ${keyFieldWidth} auto`;
                                      }
                                      else {
                                          labelStyle.flex = 'none';
                                          labelStyle.width = keyFieldWidth;
                                      }
                                  }

                                  newFieldCache[ fieldId ] = {
                                      cacheParameters,
                                      field: <div className={`${wrapperClassName} yn-enumfield-key`} style={labelStyle}>
                                          <label
                                              htmlFor={`${elementId}_key`}
                                              className="block yn-sublabel"
                                          >
                                              {cacheParameters.value as string}
                                          </label>
                                      </div>
                                  };
                              }
                          }

                          if ( propertySet?.isValueSettable ) {
                              fieldId = `${elementId}_value_label`;
                              existingFieldIds.push( fieldId );

                              cacheParameters = {
                                  originalStringKey: 'valueLabel',
                                  statusId         : 0,
                                  fieldHasError    : false,
                                  fieldIsDirty     : false,
                                  inputClassNames,
                                  key              : 'valueLabel',
                                  value            : valueLabel,
                                  language         : user ? user.language : undefined,
                                  fieldWidth       : valueFieldWidth
                              };
                              cacheParameterDiff = objectUtils.getDiffKeys( newFieldCache[ fieldId ]?.cacheParameters ?? {}, cacheParameters );

                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  labelStyle = {};

                                  if ( valueFieldWidth !== '1' ) {
                                      if ( String( Number( valueFieldWidth ) ) === valueFieldWidth ) {
                                          labelStyle.flex = `${valueFieldWidth} ${valueFieldWidth} auto`;
                                      }
                                      else {
                                          labelStyle.flex = 'none';
                                          labelStyle.width = valueFieldWidth;
                                      }
                                  }

                                  newFieldCache[ fieldId ] = {
                                      cacheParameters,
                                      field: <div className={`${wrapperClassName} yn-enumfield-value`} style={labelStyle}>
                                          <label
                                              htmlFor={`${elementId}_value`}
                                              className="block yn-sublabel"
                                          >
                                              {cacheParameters.value as string}
                                          </label>
                                      </div>
                                  };
                              }
                          }

                          // fields
                          for ( let i = 0; i < valueToUseForCaching.length; i++ ) {
                              const entry = valueToUseForCaching[ i ];

                              if ( !deletedStatusIds.includes( entry.statusId ) ) {
                                  const entryId                       = `entry_${elementId}_${entry.uniqueId}`,
                                        originalValueFor              = originalValue
                                            ? Object.values( originalValue ).find( v => v.id === entry.id )
                                            : undefined,
                                        keyStyleClassNames : string[] = getEntryStyleClassNames( keyStyle, entry ),
                                        valueStyleClassNames          = getEntryStyleClassNames( valueStyle, entry );

                                  if ( propertySet?.isKeySettable ) {
                                      fieldId = `${entryId}_key`;
                                      existingFieldIds.push( fieldId );

                                      const fieldHasError = !!errors[ entry.originalStringKey ]?.[ 'key' ],
                                            fieldIsDirty  = markDirty
                                                && (
                                                    entry.id < 0
                                                    || (
                                                        originalValueFor !== undefined
                                                        && entry.key !== originalValueFor.key
                                                    )
                                                );

                                      cacheParameters = {
                                          originalStringKey   : entry.originalStringKey,
                                          statusId            : entry.statusId,
                                          appendToElementId,
                                          disabled,
                                          fieldHasError,
                                          fieldIsDirty,
                                          inputClassNames,
                                          key                 : entry.key,
                                          value               : entry.value,
                                          language            : user ? user.language : undefined,
                                          fieldWidth          : keyFieldWidth,
                                          entryStyleClassNames: keyStyleClassNames.join( ' ' )
                                      };
                                      cacheParameterDiff = objectUtils.getDiffKeys( newFieldCache[ fieldId ]?.cacheParameters ?? {}, cacheParameters );

                                      // if no cache or there is a diff other than only for value
                                      if ( cacheParameterDiff.length > 1 || ( cacheParameterDiff.length && cacheParameterDiff[ 0 ] !== 'value' ) ) {
                                          const field = getEditor(
                                              'key',
                                              fieldId,
                                              entry,
                                              fieldHasError,
                                              fieldIsDirty,
                                              keyFieldWidth,
                                              keyEditorParams,
                                              ( tabIndex ?? 0 ) + ( 4 * i ) + 1,
                                              undefined,
                                              keyStyleClassNames
                                          );

                                          if ( field ) {
                                              changed = true;

                                              newFieldCache[ fieldId ] = {
                                                  cacheParameters,
                                                  field
                                              };
                                          }
                                      }
                                  }

                                  if ( propertySet?.isValueSettable ) {
                                      fieldId = `${entryId}_value`;
                                      existingFieldIds.push( fieldId );

                                      const fieldHasError = !!errors[ entry.originalStringKey ]?.[ 'value' ],
                                            fieldIsDirty  = markDirty
                                                && (
                                                    entry.id < 0
                                                    || (
                                                        originalValueFor !== undefined
                                                        && entry.value !== originalValueFor.value
                                                    )
                                                );

                                      cacheParameters = {
                                          originalStringKey   : entry.originalStringKey,
                                          statusId            : entry.statusId,
                                          appendToElementId,
                                          disabled,
                                          fieldHasError,
                                          fieldIsDirty,
                                          inputClassNames,
                                          key                 : entry.key,
                                          value               : entry.value,
                                          language            : user ? user.language : undefined,
                                          fieldWidth          : valueFieldWidth,
                                          entryStyleClassNames: valueStyleClassNames.join( ' ' )
                                      };
                                      cacheParameterDiff = objectUtils.getDiffKeys( newFieldCache[ fieldId ]?.cacheParameters ?? {}, cacheParameters );

                                      // if no cache or there is a diff other than only for key or key was empty
                                      if ( cacheParameterDiff.length > 1 || ( cacheParameterDiff.length && ( cacheParameterDiff[ 0 ] !== 'key' || !newFieldCache[ fieldId ]?.cacheParameters.key ) ) ) {
                                          const field = getEditor(
                                              'value',
                                              fieldId,
                                              entry,
                                              fieldHasError,
                                              fieldIsDirty,
                                              valueFieldWidth,
                                              valueEditorParams,
                                              ( tabIndex ?? 0 ) + ( 4 * i ) + 2,
                                              undefined,
                                              valueStyleClassNames
                                          );

                                          if ( field ) {
                                              changed = true;

                                              newFieldCache[ fieldId ] = {
                                                  cacheParameters,
                                                  field
                                              };
                                          }
                                      }
                                  }
                              }
                          }

                          // cleanup non existingFieldIds
                          for ( fieldId of Object.keys( newFieldCache ) as string[] ) {
                              if ( !existingFieldIds.includes( fieldId ) ) {
                                  changed = true;
                                  delete newFieldCache[ fieldId ];
                              }
                          }

                          if ( changed ) {
                              currentFieldCache.current = newFieldCache;
                          }
                      }

                      return currentFieldCache.current;
                  },
                  [
                      appendToElementId,
                      deletedStatusIds,
                      disabled,
                      errorMessage, // check if errors really changed
                      getEditor,
                      getEntryStyleClassNames,
                      inputClassNames,
                      keyFieldWidth,
                      keyLabel,
                      keyStyle,
                      JSON.stringify( originalValue ), // check if originalValue really changed
                      presentation,
                      user ? user.language : undefined,
                      valueFieldWidth,
                      valueLabel,
                      valueStyle,
                      valueToUseForCaching
                  ]
              ),
              columnsCache : ColumnsCache                                                 = useMemo(
                  () => {
                      if ( presentation === 'grid' || presentation === 'grid_reversed' ) {
                          const newColumnsCache : ColumnsCache = { ...currentColumnsCache.current },
                                columnDefaults                 = {
                                    filter         : false,
                                    reorderable    : false,
                                    resizeable     : false,
                                    sortable       : false,
                                    bodyClassName  : 'yn-nowrap-top-column yn-nopadding-column',
                                    headerClassName: 'yn-header-column'
                                },
                                existingCellIds : string[]     = [];

                          let changed : boolean             = false,
                              columnId : keyof ColumnsCache | undefined,
                              labelStyle : React.CSSProperties | undefined,
                              cacheParameters : CacheParameters | undefined,
                              cacheParameterDiff : string[] = [];

                          // Columns
                          columnId = 'delete';
                          cacheParameters = {
                              originalStringKey: `${columnId}_header`,
                              statusId         : 0,
                              fieldHasError    : false,
                              fieldIsDirty     : false,
                              inputClassNames,
                              key              : `${columnId}_header`,
                              value            : '',
                              language         : user ? user.language : undefined
                          };
                          cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );

                          if ( cacheParameterDiff.length ) {
                              changed = true;

                              let header : string | JSX.Element = '';

                              if ( extensible ) {
                                  header = <Button
                                      onClick={onAddRowButtonClick}
                                      disabled={disabled || !!valueToUseForCaching.find( v => v.id === 0 )}
                                      icon="pi pi-plus-circle"
                                      className="yn-action-button yn-green-button"
                                  />;
                              }

                              newColumnsCache[ columnId ] = {
                                  columnCache: {
                                      cacheParameters,
                                      column: <Column
                                          key={columnId}
                                          columnKey={columnId}
                                          field={`${columnId}Body`}
                                          header={header}

                                          {...columnDefaults}

                                          bodyClassName={`${columnDefaults.bodyClassName} yn-action-column`}
                                          headerClassName="yn-header-action-column"
                                          filterHeaderClassName="yn-header-action-column"
                                      />
                                  },
                                  cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                              };
                          }

                          columnId = 'key';
                          if ( propertySet?.isKeySettable ) {
                              cacheParameters = {
                                  originalStringKey: `${columnId}_header`,
                                  statusId         : 0,
                                  fieldHasError    : false,
                                  fieldIsDirty     : false,
                                  inputClassNames,
                                  key              : `${columnId}_header`,
                                  value            : keyLabel,
                                  language         : user ? user.language : undefined,
                                  fieldWidth       : keyFieldWidth
                              };
                              cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );

                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  labelStyle = {};

                                  if ( keyFieldWidth !== '1' && String( Number( keyFieldWidth ) ) !== keyFieldWidth ) {
                                      labelStyle.width = keyFieldWidth;
                                  }

                                  newColumnsCache[ columnId ] = {
                                      columnCache: {
                                          cacheParameters,
                                          column: <Column
                                              key={columnId}
                                              columnKey={columnId}
                                              field={`${columnId}Body`}
                                              header={keyLabel}

                                              {...columnDefaults}

                                              style={labelStyle}
                                              editor={cellEditor}
                                          />
                                      },
                                      cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                                  };
                              }
                          }
                          else if ( newColumnsCache[ columnId ] ) {
                              changed = true;

                              delete newColumnsCache[ columnId ];
                          }

                          columnId = 'value';
                          if ( propertySet?.isValueSettable ) {
                              cacheParameters = {
                                  originalStringKey: `${columnId}_header`,
                                  statusId         : 0,
                                  fieldHasError    : false,
                                  fieldIsDirty     : false,
                                  inputClassNames,
                                  key              : `${columnId}_header`,
                                  value            : keyLabel,
                                  language         : user ? user.language : undefined,
                                  fieldWidth       : valueFieldWidth
                              };
                              cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );

                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  labelStyle = {};

                                  if ( valueFieldWidth !== '1' && String( Number( valueFieldWidth ) ) !== valueFieldWidth ) {
                                      labelStyle.width = valueFieldWidth;
                                  }

                                  newColumnsCache[ columnId ] = {
                                      columnCache: {
                                          cacheParameters,
                                          column: <Column
                                              key={columnId}
                                              columnKey={columnId}
                                              field={`${columnId}Body`}
                                              header={valueLabel}

                                              {...columnDefaults}

                                              style={labelStyle}
                                              editor={cellEditor}
                                          />
                                      },
                                      cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                                  };
                              }
                          }
                          else if ( newColumnsCache[ columnId ] ) {
                              changed = true;

                              delete newColumnsCache[ columnId ];
                          }

                          columnId = 'sort';
                          if ( showSorter ) {
                              cacheParameters = {
                                  originalStringKey: `${columnId}_header`,
                                  statusId         : 0,
                                  fieldHasError    : false,
                                  fieldIsDirty     : false,
                                  inputClassNames,
                                  key              : `${columnId}_header`,
                                  value            : '',
                                  language         : user ? user.language : undefined
                              };
                              cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );

                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  newColumnsCache[ columnId ] = {
                                      columnCache: {
                                          cacheParameters,
                                          column: <Column
                                              key={columnId}
                                              columnKey={columnId}
                                              field={columnId}
                                              header=""

                                              {...columnDefaults}

                                              rowReorder
                                              bodyClassName={`${columnDefaults.bodyClassName} yn-action-column`}
                                              headerClassName="yn-header-action-column"
                                              filterHeaderClassName="yn-header-action-column"
                                          />
                                      },
                                      cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                                  };
                              }
                          }
                          else if ( newColumnsCache[ columnId ] ) {
                              changed = true;

                              delete newColumnsCache[ columnId ];
                          }

                          // Cells
                          for ( let i = 0; i < valueToUseForCaching.length; i++ ) {
                              const entry = valueToUseForCaching[ i ];

                              if ( !deletedStatusIds.includes( entry.statusId ) ) {
                                  const rowId                         = `row_${elementId}_${entry.uniqueId}`,
                                        originalValueFor              = originalValue
                                            ? Object.values( originalValue ).find( v => v.id === entry.id )
                                            : undefined,
                                        keyStyleClassNames : string[] = getEntryStyleClassNames( keyStyle, entry ),
                                        valueStyleClassNames          = getEntryStyleClassNames( valueStyle, entry );

                                  let cellId : string | undefined,
                                      rendererClassNames : string[],
                                      body : React.ReactNode | React.ReactNode[],
                                      editor : React.ReactNode | React.ReactNode[];

                                  columnId = 'delete';
                                  cellId = `${rowId}_${columnId}`;
                                  existingCellIds.push( cellId );

                                  cacheParameters = {
                                      originalStringKey: cellId,
                                      statusId         : 0,
                                      disabled,
                                      fieldHasError    : false,
                                      fieldIsDirty     : false,
                                      inputClassNames,
                                      key              : cellId,
                                      value            : `${entry.id}, ${entry.originalStringKey}`,
                                      language         : user ? user.language : undefined
                                  };
                                  cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.cellCache?.[ cellId ]?.cacheParameters ?? {}, cacheParameters );

                                  // if no cache or there is a diff other than only for value
                                  if ( cacheParameterDiff.length ) {
                                      changed = true;

                                      if ( entry.id !== 0 ) {
                                          body = <Button
                                              onClick={!disabled ? onDeleteButtonClick.bind( null, entry.originalStringKey ) : undefined}
                                              disabled={disabled}
                                              icon="pi pi-minus-circle"
                                              className="p-button-rounded p-button-danger p-button-text"
                                              tabIndex={( tabIndex ?? 0 ) + ( 4 * i )}
                                          />;
                                      }
                                      else {
                                          body = '';
                                      }

                                      newColumnsCache[ columnId ] = {
                                          columnCache: newColumnsCache[ columnId ]?.columnCache ?? {
                                              cacheParameters,
                                              column: ''
                                          },
                                          cellCache  : {
                                              ...newColumnsCache[ columnId ]?.cellCache,
                                              [ cellId ]: {
                                                  cacheParameters,
                                                  body
                                              }
                                          }
                                      };
                                  }

                                  columnId = 'key';
                                  if ( propertySet?.isKeySettable ) {
                                      cellId = `${rowId}_${columnId}`;
                                      existingCellIds.push( cellId );

                                      const fieldHasError = !!errors[ entry.originalStringKey ]?.key,
                                            fieldIsDirty  = markDirty
                                                && (
                                                    entry.id < 0
                                                    || (
                                                        originalValueFor !== undefined
                                                        && entry.key !== originalValueFor.key
                                                    )
                                                ),
                                            fieldIsEmpty  = entry.key === undefined || entry.key === null || entry.key === '';

                                      cacheParameters = {
                                          originalStringKey   : entry.originalStringKey,
                                          statusId            : entry.statusId,
                                          appendToElementId,
                                          disabled,
                                          fieldHasError,
                                          fieldIsDirty,
                                          inputClassNames,
                                          key                 : entry.key,
                                          value               : entry.value,
                                          language            : user ? user.language : undefined,
                                          fieldWidth          : keyFieldWidth,
                                          entryStyleClassNames: keyStyleClassNames.join( ' ' )
                                      };
                                      cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.cellCache?.[ cellId ]?.cacheParameters ?? {}, cacheParameters );

                                      // if no cache or there is a diff other than only for value
                                      if ( cacheParameterDiff.length > 1 || ( cacheParameterDiff.length && cacheParameterDiff[ 0 ] !== 'value' ) ) {
                                          changed = true;

                                          // the cell renders the key read-only until it is clicked, so the
                                          // conditional style has to reach the renderer as well as the editor
                                          rendererClassNames = [ 'yn-cell-renderer-el', ...keyStyleClassNames ];

                                          if ( disabled ) {
                                              rendererClassNames.push( 'p-disabled' );

                                              if ( propertySet.keyType === 'EMAIL' || propertySet.keyType === 'LINK' || propertySet.keyType === 'PHONE' ) {
                                                  rendererClassNames.push( 'yn-enabled-button' );
                                              }
                                          }
                                          else {
                                              // error marking
                                              if ( fieldHasError ) {
                                                  rendererClassNames.push( 'yn-error-border' );
                                              }

                                              // dirty marking
                                              if ( fieldIsDirty
                                                  || (
                                                      entry.id === 0
                                                      && !fieldIsEmpty
                                                  )
                                              ) {
                                                  rendererClassNames.push( 'yn-dirty-border' );
                                              }
                                          }

                                          if ( !fieldIsEmpty ) {
                                              body = [
                                                  statusColoringRenderer(
                                                      renderValue(
                                                          propertySet.keyType,
                                                          entry.key,
                                                          {
                                                              language: user ? user.language : undefined
                                                          }
                                                      ),
                                                      entry.statusId,
                                                      schema,
                                                      cellId,
                                                      cellId,
                                                      rendererClassNames.join( ' ' )
                                                  )
                                              ];
                                          }
                                          else {
                                              body = [
                                                  <div
                                                      key={cellId}
                                                      id={cellId}
                                                      className={`${rendererClassNames.join( ' ' )} yn-secondary`}
                                                  >
                                                      {resolvedPlaceholder}
                                                  </div>
                                              ];
                                          }

                                          if ( !disabled ) {
                                              editor = [
                                                  getEditor(
                                                      'key',
                                                      cellId,
                                                      entry,
                                                      fieldHasError,
                                                      fieldIsDirty,
                                                      keyFieldWidth,
                                                      keyEditorParams,
                                                      ( tabIndex ?? 0 ) + ( 4 * i ) + 1,
                                                      'yn-cell-editor',
                                                      keyStyleClassNames
                                                  )
                                              ];
                                          }

                                          if ( fieldHasError ) {
                                              const tooltip = <Tooltip
                                                  target={`#${cellId}`}
                                                  position="top"
                                                  className="yn-tooltip"
                                              >
                                                  <div className="yn-tooltip-error">
                                                      <div className="yn-tooltip-header">
                                                          {__( 'YN.base.common.tooltip.error.title' )}
                                                      </div>
                                                      {parse( __(
                                                          errors[ entry.originalStringKey ]?.[ 'key' ].errorMessage,
                                                          errors[ entry.originalStringKey ]?.[ 'key' ].errorPlaceholders
                                                      ) )}
                                                  </div>
                                              </Tooltip>;

                                              ( body as React.ReactNode[] ).push( tooltip );

                                              if ( editor ) {
                                                  ( editor as React.ReactNode[] ).push( tooltip );
                                              }
                                          }

                                          newColumnsCache[ columnId ] = {
                                              columnCache: newColumnsCache[ columnId ]?.columnCache ?? {
                                                  cacheParameters,
                                                  column: ''
                                              },
                                              cellCache  : {
                                                  ...newColumnsCache[ columnId ]?.cellCache,
                                                  [ cellId ]: {
                                                      cacheParameters,
                                                      body,
                                                      editor
                                                  }
                                              }
                                          };
                                      }
                                  }

                                  columnId = 'value';
                                  if ( propertySet?.isValueSettable ) {
                                      cellId = `${rowId}_${columnId}`;
                                      existingCellIds.push( cellId );

                                      const fieldHasError = !!errors[ entry.originalStringKey ]?.value,
                                            fieldIsDirty  = markDirty
                                                && (
                                                    entry.id < 0
                                                    || (
                                                        originalValueFor !== undefined
                                                        && entry.value !== originalValueFor.value
                                                    )
                                                ),
                                            fieldIsEmpty  = entry.value === undefined || entry.value === null || entry.value === '';

                                      cacheParameters = {
                                          originalStringKey   : entry.originalStringKey,
                                          statusId            : entry.statusId,
                                          appendToElementId,
                                          disabled,
                                          fieldHasError,
                                          fieldIsDirty,
                                          inputClassNames,
                                          key                 : entry.key,
                                          value               : entry.value,
                                          language            : user ? user.language : undefined,
                                          fieldWidth          : valueFieldWidth,
                                          entryStyleClassNames: valueStyleClassNames.join( ' ' )
                                      };
                                      cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.cellCache?.[ cellId ]?.cacheParameters ?? {}, cacheParameters );

                                      // if no cache or there is a diff other than only for key
                                      if ( cacheParameterDiff.length > 1 || ( cacheParameterDiff.length && cacheParameterDiff[ 0 ] !== 'key' ) ) {
                                          changed = true;

                                          rendererClassNames = [ 'yn-cell-renderer-el', ...valueStyleClassNames ];

                                          if ( disabled ) {
                                              rendererClassNames.push( 'p-disabled' );

                                              if ( propertySet.valueType === 'EMAIL' || propertySet.valueType === 'LINK' || propertySet.valueType === 'PHONE' ) {
                                                  rendererClassNames.push( 'yn-enabled-button' );
                                              }
                                          }
                                          else {
                                              // error marking
                                              if ( fieldHasError ) {
                                                  rendererClassNames.push( 'yn-error-border' );
                                              }

                                              // dirty marking
                                              if ( fieldIsDirty ) {
                                                  rendererClassNames.push( 'yn-dirty-border' );
                                              }
                                          }

                                          if ( !fieldIsEmpty ) {
                                              body = [
                                                  statusColoringRenderer(
                                                      renderValue(
                                                          propertySet.valueType,
                                                          entry.value,
                                                          {
                                                              language : user ? user.language : undefined,
                                                              valueUnit: propertySet.valueUnit
                                                          }
                                                      ),
                                                      entry.statusId,
                                                      schema,
                                                      cellId,
                                                      cellId,
                                                      rendererClassNames.join( ' ' )
                                                  )
                                              ];
                                          }
                                          else {
                                              body = [
                                                  <div
                                                      key={cellId}
                                                      id={cellId}
                                                      className={`${rendererClassNames.join( ' ' )} yn-secondary`}
                                                  >
                                                      {!propertySet.isKeySettable ? resolvedPlaceholder : parse( '&nbsp;' )}
                                                  </div>
                                              ];
                                          }

                                          if ( !disabled ) {
                                              editor = [
                                                  getEditor(
                                                      'value',
                                                      cellId,
                                                      entry,
                                                      fieldHasError,
                                                      fieldIsDirty,
                                                      valueFieldWidth,
                                                      valueEditorParams,
                                                      ( tabIndex ?? 0 ) + ( 4 * i ) + 2,
                                                      'yn-cell-editor',
                                                      valueStyleClassNames
                                                  )
                                              ];
                                          }

                                          if ( fieldHasError ) {
                                              const tooltip = <Tooltip
                                                  target={`#${cellId}`}
                                                  position="top"
                                                  className="yn-tooltip"
                                              >
                                                  <div className="yn-tooltip-error">
                                                      <div className="yn-tooltip-header">
                                                          {__( 'YN.base.common.tooltip.error.title' )}
                                                      </div>
                                                      {parse( __(
                                                          errors[ entry.originalStringKey ]?.[ 'value' ].errorMessage,
                                                          errors[ entry.originalStringKey ]?.[ 'value' ].errorPlaceholders
                                                      ) )}
                                                  </div>
                                              </Tooltip>;

                                              ( body as React.ReactNode[] ).push( tooltip );

                                              if ( editor ) {
                                                  ( editor as React.ReactNode[] ).push( tooltip );
                                              }
                                          }

                                          newColumnsCache[ columnId ] = {
                                              columnCache: newColumnsCache[ columnId ]?.columnCache ?? {
                                                  cacheParameters,
                                                  column: ''
                                              },
                                              cellCache  : {
                                                  ...newColumnsCache[ columnId ]?.cellCache,
                                                  [ cellId ]: {
                                                      cacheParameters,
                                                      body,
                                                      editor
                                                  }
                                              }
                                          };
                                      }
                                  }
                              }
                          }

                          // cleanup not existingCellIds
                          for ( const columnCache of Object.values( newColumnsCache ) ) {
                              for ( const cellId of Object.keys( columnCache.cellCache ) as string[] ) {
                                  if ( !existingCellIds.includes( cellId ) ) {
                                      changed = true;
                                      delete columnCache.cellCache[ cellId ];
                                  }
                              }
                          }

                          if ( changed ) {
                              currentColumnsCache.current = newColumnsCache;
                          }
                      }

                      return currentColumnsCache.current;
                  },
                  [
                      appendToElementId,
                      deletedStatusIds,
                      disabled,
                      errorMessage, // check if errors really changed
                      extensible,
                      getEditor,
                      getEntryStyleClassNames,
                      inputClassNames,
                      keyFieldWidth,
                      keyLabel,
                      keyStyle,
                      JSON.stringify( originalValue ), // check if originalValue really changed
                      presentation,
                      resolvedPlaceholder,
                      schema,
                      user ? user.language : undefined,
                      valueFieldWidth,
                      valueLabel,
                      valueStyle,
                      valueToUseForCaching
                  ]
              ),
              rowData : RowData[]                                                         = useMemo(
                  () => {
                      const newRowData : RowData[] = [];

                      for ( const valueForRow of valueToUseForCaching ) {
                          const rowId = `row_${elementId}_${valueForRow.uniqueId}`;

                          newRowData.push( {
                              ...valueForRow,
                              deleteBody: currentColumnsCache.current?.[ 'delete' ]?.cellCache[ `${rowId}_delete` ]?.body,
                              keyBody   : currentColumnsCache.current?.[ 'key' ]?.cellCache[ `${rowId}_key` ]?.body,
                              valueBody : currentColumnsCache.current?.[ 'value' ]?.cellCache[ `${rowId}_value` ]?.body
                          } );
                      }

                      return newRowData;
                  },
                  [
                      columnsCache,
                      valueToUseForCaching
                  ]
              ),
              chipsCache : ChipsCache                                                     = useMemo(
                  () => {
                      if ( presentation === 'chips' && propertySet ) {
                          const newChipsCache : ChipsCache = { ...currentChipsCache.current },
                                existingChipIds : string[] = [];

                          let changed : boolean = false;

                          for ( let i = 0; i < valueToUseForCaching.length; i++ ) {
                              const entry  = valueToUseForCaching[ i ],
                                    chipId = `chip_${elementId}_${entry.uniqueId}`;

                              if ( !deletedStatusIds.includes( entry.statusId ) ) {
                                  const originalValueFor              = originalValue
                                            ? Object.values( originalValue ).find( v => v.id === entry.id )
                                            : undefined,
                                        fieldHasError                 = !!errors[ entry.originalStringKey ],
                                        fieldIsDirty                  = markDirty
                                            && (
                                                entry.id < 0
                                                || (
                                                    originalValueFor !== undefined
                                                    && (
                                                        ( propertySet.isKeySettable && entry.key !== originalValueFor.key )
                                                        || ( !propertySet.isKeySettable && entry.value !== originalValueFor.value )
                                                    )
                                                )
                                            ),
                                        fieldIsEmpty                  = propertySet.isKeySettable
                                            ? entry.key === undefined || entry.key === null || entry.key === ''
                                            : entry.value === undefined || entry.value === null || entry.value === '',
                                        // a chip shows the one column the property lets the user set, so
                                        // that column's conditional style is the one painting its text
                                        shownStyleClassNames          = getEntryStyleClassNames(
                                            propertySet.isKeySettable ? keyStyle : valueStyle,
                                            entry
                                        ),
                                        rowStyleClassNames            = getEntryStyleClassNames( rowStyle, entry, enumEntryRowStyleSelectors ),
                                        cacheParameters               = {
                                            originalStringKey   : entry.originalStringKey,
                                            statusId            : entry.statusId,
                                            fieldHasError,
                                            fieldIsDirty,
                                            inputClassNames,
                                            key                 : entry.key,
                                            value               : entry.value,
                                            language            : user ? user.language : undefined,
                                            entryStyleClassNames: shownStyleClassNames.concat( rowStyleClassNames ).join( ' ' )
                                        },
                                        cacheParameterDiff            = objectUtils.getDiffKeys( newChipsCache[ chipId ]?.cacheParameters ?? {}, cacheParameters ),
                                        rendererClassNames : string[] = [ 'yn-cell-renderer-el', ...shownStyleClassNames ];

                                  existingChipIds.push( chipId );

                                  if ( cacheParameterDiff.length ) {
                                      changed = true;

                                      let item : React.ReactNode,
                                          errorMessageForItem : string | undefined;

                                      if ( !fieldIsEmpty ) {
                                          item = statusColoringRenderer(
                                              renderValue(
                                                  propertySet.isKeySettable ? propertySet.keyType : propertySet.valueType,
                                                  propertySet.isKeySettable ? entry.key : entry.value,
                                                  {
                                                      language: user ? user.language : undefined
                                                  }
                                              ),
                                              entry.statusId,
                                              schema,
                                              undefined,
                                              chipId,
                                              rendererClassNames.join( ' ' ),
                                              ( tabIndex ?? 0 ) + i
                                          );
                                      }
                                      else {
                                          item = <div className={rendererClassNames.join( ' ' )}>&nbsp;</div>
                                      }

                                      if ( fieldHasError ) {
                                          errorMessageForItem = __(
                                              errors[ entry.originalStringKey ]![ Object.keys( errors[ entry.originalStringKey ]! )[ 0 ] ].errorMessage,
                                              errors[ entry.originalStringKey ]![ Object.keys( errors[ entry.originalStringKey ]! )[ 0 ] ].errorPlaceholders
                                          );
                                      }

                                      newChipsCache[ chipId ] = {
                                          cacheParameters,
                                          item,
                                          errorMessage: errorMessageForItem,
                                          isDirty     : fieldIsDirty,
                                          tabIndex    : ( tabIndex ?? 0 ) + i,
                                          rowStyleClassNames
                                      };
                                  }
                              }
                          }

                          // cleanup not existingChipIds
                          for ( const chipId of Object.keys( newChipsCache ) as string[] ) {
                              if ( !existingChipIds.includes( chipId ) ) {
                                  changed = true;
                                  delete newChipsCache[ chipId ];
                              }
                          }

                          if ( changed ) {
                              currentChipsCache.current = newChipsCache;
                          }
                      }

                      return currentChipsCache.current;
                  },
                  [
                      deletedStatusIds,
                      errorMessage, // check if errors really changed
                      getEntryStyleClassNames,
                      inputClassNames,
                      keyStyle,
                      JSON.stringify( originalValue ), // check if originalValue really changed
                      presentation,
                      resolvedPlaceholder,
                      rowStyle,
                      schema,
                      user ? user.language : undefined,
                      valueStyle,
                      valueToUseForCaching
                  ]
              ),
              fieldWrapperErrorMessage : string | JSX.Element | JSX.Element[] | undefined = useMemo(
                  () => {
                      let retVal : string | JSX.Element | JSX.Element[] | undefined;

                      if ( presentation === 'chips' && Object.keys( errors ).length ) {
                          const firstError          = errors[ Object.keys( errors )[ 0 ] ],
                                firstErrorAttribute = Object.values( firstError ?? {} )[ 0 ];

                          if ( firstErrorAttribute ) {
                              retVal = parse( __(
                                  firstErrorAttribute.errorMessage,
                                  firstErrorAttribute.errorPlaceholders
                              ) );
                          }
                      }

                      return retVal;
                  },
                  [
                      errorMessage, // check if errors really changed
                      presentation
                  ]
              ),
              fieldWrapperIsDirty : boolean | undefined                                   = useMemo(
                  () => {
                      let retVal : boolean | undefined;

                      if ( presentation === 'chips' ) {

                          retVal = JSON.stringify( value ) !== JSON.stringify( originalValue );

                          if ( !retVal ) {
                              for ( const chip of Object.values( chipsCache ) ) {
                                  if ( chip.isDirty ) {
                                      retVal = true;
                                      break;
                                  }
                              }
                          }
                      }

                      return retVal;
                  },
                  [
                      chipsCache,
                      originalValue,
                      presentation,
                      value
                  ]
              ),
              fieldContent : JSX.Element | JSX.Element[ ] | undefined                     = useMemo(
                  () => {
                      let retValElements : JSX.Element | JSX.Element[] | undefined,
                          heightToUse = height;

                      if ( presentation === 'fieldList' || presentation === 'fieldList_reversed' ) {
                          const keyFieldLabel   = fieldCache[ `${elementId}_key_label` ]?.field,
                                valueFieldLabel = fieldCache[ `${elementId}_value_label` ]?.field;

                          // labels for key and/or value
                          retValElements = [
                              <div key="label" className="yn-enumfield-entry yn-enumfield-entry-label">
                                  <span className="yn-enumfield-delete-button-placeholder"/>

                                  {keyFieldLabel}
                                  {valueFieldLabel}

                                  {showSorter &&
                                      <span className="yn-enumfield-changeorder-button-placeholder"/>
                                  }
                              </div>
                          ];

                          for ( let i = 0; i < valueToUseForCaching.length; i++ ) {
                              const entry = valueToUseForCaching[ i ];

                              if ( !deletedStatusIds.includes( entry.statusId ) ) {
                                  const entryId          = `entry_${elementId}_${entry.uniqueId}`,
                                        originalValueFor = originalValue
                                            ? Object.values( originalValue ).find( v => v.id === entry.id )
                                            : undefined;

                                  let keyField : JSX.Element | undefined,
                                      valueField : JSX.Element | undefined;

                                  if ( propertySet?.isKeySettable ) {
                                      const fieldId = `${entryId}_key`;

                                      keyField = fieldCache[ fieldId ]?.field;
                                  }

                                  if ( propertySet?.isValueSettable ) {
                                      const fieldId = `${entryId}_value`;

                                      valueField = fieldCache[ fieldId ]?.field;
                                  }

                                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                  if ( keyField || valueField ) {
                                      const sorterClassNames : string[] = [ 'p-button-rounded_x', 'p-button-secondary', 'p-button-text', 'yn-enumfield-changeorder-button' ];

                                      if ( markDirty && originalValueFor !== undefined && entry.orderNum !== originalValueFor.orderNum ) {
                                          sorterClassNames.push( 'yn-dirty' );
                                      }
                                      if ( disabled ) {
                                          sorterClassNames.push( 'p-disabled' );
                                      }

                                      // fields for key and/or value
                                      retValElements.push(
                                          <div
                                              key={entryId}
                                              className={[
                                                  'yn-enumfield-entry',
                                                  ...( extensible && i === valueToUseForCaching.length - 1 ? [ 'yn-enumfield-entry-new' ] : [] ),
                                                  ...getEntryStyleClassNames( rowStyle, entry, enumEntryRowStyleSelectors )
                                              ].join( ' ' )}
                                          >
                                              {   // not new entry -> delete button
                                                  entry.id !== 0 &&
                                                  <Button
                                                      disabled={disabled}
                                                      icon="pi pi-times"
                                                      className="p-button-danger p-button-text yn-enumfield-delete-button"
                                                      onClick={!disabled ? onDeleteButtonClick.bind( null, entry.originalStringKey ) : undefined}
                                                      tabIndex={( tabIndex ?? 0 ) + ( 4 * i )}
                                                  />
                                              }
                                              {   // new entry -> delete button placeholder
                                                  entry.id === 0 &&
                                                  <span className="yn-enumfield-delete-button-placeholder"/>
                                              }

                                              {keyField}
                                              {valueField}

                                              {   // show sorter and not new entry -> sorter - disable on any error
                                                  showSorter && entry.id !== 0 &&
                                                  <Button
                                                      disabled={disabled}
                                                      icon="pi pi-bars"
                                                      className={sorterClassNames.join( ' ' )}
                                                      onMouseDown={!disabled ? onSorterMouseDown.bind( null, entry.originalStringKey ) : undefined}
                                                      onMouseUp={!disabled ? onSorterDragEnd : undefined}
                                                      onKeyDown={!disabled ? onSorterKeyDown.bind( null, entry.originalStringKey ) : undefined}
                                                      tabIndex={( tabIndex ?? 0 ) + ( 4 * i ) + 3}
                                                  />
                                              }
                                              {   // show sorter and new entry -> placeholder
                                                  showSorter && entry.id === 0 &&
                                                  <span className="yn-enumfield-changeorder-button-placeholder"/>
                                              }
                                          </div>
                                      );

                                      if ( errors[ entry.originalStringKey ] ) {
                                          retValElements.push(
                                              <div key={`${entryId}_error_wrapper`} className="yn-enumfield-entry-error">
                                                  <span className="yn-enumfield-delete-button-placeholder"/>

                                                  <FieldWrapper
                                                      uid={`${entryId}_error`}
                                                      labelVisibility="hidden"
                                                      labelPosition="top"
                                                      infoTextLayout="text"
                                                      autoComplete={false}
                                                      errorMessage={Object.keys( errors[ entry.originalStringKey ] )
                                                          .reduce(
                                                              ( eM, attribute ) => {
                                                                  eM = eM.concat( parse( __(
                                                                      errors[ entry.originalStringKey ][ attribute ].errorMessage,
                                                                      errors[ entry.originalStringKey ][ attribute ].errorPlaceholders
                                                                  ) ) as React.ReactElement );
                                                                  return eM;
                                                              },
                                                              [] as React.ReactElement[]
                                                          )
                                                      }
                                                      pt={pt?.fieldWrapper}
                                                  />

                                                  {   // show sorter -> placeholder
                                                      showSorter &&
                                                      <span className="yn-enumfield-changeorder-button-placeholder"/>
                                                  }
                                              </div>
                                          );
                                      }
                                  }
                              }
                          }
                      }

                      else if ( presentation === 'grid' || presentation === 'grid_reversed' ) {
                          const columns : React.ReactNode[] = [];

                          if ( columnsCache.key?.columnCache.column ) {
                              columns.push( columnsCache.key.columnCache.column );
                          }
                          if ( columnsCache.value?.columnCache.column ) {
                              if ( presentation === 'grid_reversed' ) {
                                  columns.unshift( columnsCache.value.columnCache.column );
                              }
                              else {
                                  columns.push( columnsCache.value.columnCache.column );
                              }
                          }

                          if ( columnsCache.delete?.columnCache.column ) {
                              columns.unshift( columnsCache.delete.columnCache.column );
                          }
                          if ( columnsCache.sort?.columnCache.column ) {
                              columns.push( columnsCache.sort.columnCache.column );
                          }


                          retValElements = <DataTable
                              key={`${elementId}_dataTable`}
                              id={`${elementId}_dataTable`}

                              value={rowData}
                              dataKey="uniqueId"

                              emptyMessage=" "

                              rowClassName={onResolveRowClass}
                              className="p-datatable-striped yn-datatable-border yn-datatable-no-last-row-border"
                              style={{ width: '100%', zIndex: 1 }}

                              columnResizeMode="expand"

                              scrollable
                              scrollHeight="flex"

                              lazy

                              reorderableRows={!disabled && listingOrder === 'order'}
                              onRowReorder={onRowReorder}

                              pt={{
                                  wrapper: {
                                      style: {
                                          '--table-width'        : '100%',
                                          '--table-header-height': '2.2rem'
                                      }
                                  },
                                  table  : {
                                      style: {
                                          width      : '100%',
                                          tableLayout: 'fixed'
                                      }
                                  }
                              }}
                          >
                              {columns}
                          </DataTable>;
                      }

                      else if ( presentation === 'chips' ) {
                          if ( propertySet?.isKeySettable && propertySet.isValueSettable ) {
                              retValElements = <StaticContentField
                                  {..._.pick( props, staticContentFieldComponentProps ) as StaticContentFieldComponentProps}

                                  label={undefined}
                                  labelVisibility="hidden"
                                  infoTextLayout="text"
                                  height={1}
                                  content="YN.base.form.enumField.error.chipsNotValidForKeyAndValueSettable"
                                  inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                                  messageClassNames={[ 'hidden' ]}
                                  inputStyle={getHeightStyle( {}, 1, 0, labelVisibility, labelPosition, infoTextLayout )}
                                  style={{ marginBottom: 0 }}
                              />;

                              heightToUse = 1;
                          }
                          else if (
                              ( propertySet?.isKeySettable && propertySet.keyType !== 'STRING' )
                              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                              || ( propertySet?.isValueSettable && propertySet.valueType !== 'STRING' )
                          ) {
                              retValElements = <StaticContentField
                                  {..._.pick( props, staticContentFieldComponentProps ) as StaticContentFieldComponentProps}

                                  label={undefined}
                                  labelVisibility="hidden"
                                  infoTextLayout="text"
                                  height={1}
                                  content="YN.base.form.enumField.error.chipsNotValidForNonStringKeyOrValue"
                                  inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                                  messageClassNames={[ 'hidden' ]}
                                  inputStyle={getHeightStyle( {}, 1, 0, labelVisibility, labelPosition, infoTextLayout )}
                                  style={{ marginBottom: 0 }}
                              />;

                              heightToUse = 1;
                          }
                          else {
                              retValElements = <Chips
                                  ref={chipsComponentRef}
                                  key={`${elementId}_chips`}
                                  id={`${elementId}_chips`}

                                  // typescript definition of Chips component wrong
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  value={valueToUseForCaching.filter( entry => !deletedStatusIds.includes( entry.statusId ) )}

                                  className={inputClassNames.join( ' ' )}

                                  disabled={disabled}
                                  allowDuplicate={!propertySet?.isKeySettable}
                                  placeholder={resolvedPlaceholder}

                                  itemTemplate={chipsItemTemplate}

                                  onAdd={onChipsAdd}
                                  onRemove={onChipsRemove}
                                  onKeyDown={propertySet?.isKeySettable ? onChipsKeyDown : undefined}

                                  pt={{
                                      container: {
                                          onClickCapture: onChipsWrapperClick
                                      }
                                  }}
                              />;
                          }
                      }

                      return <div
                          id={elementId}
                          className={`yn-enumfield-wrapper yn-presentation-${presentation}${!heightToUse ? ' yn-auto-height' : ''}`}
                          style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}
                      >
                          {retValElements}
                      </div>;
                  },
                  [
                      chipsCache,
                      columnsCache,
                      deletedStatusIds,
                      disabled,
                      errorMessage, // check if errors really changed
                      fieldCache,
                      getEntryStyleClassNames,
                      JSON.stringify( heightStyle ),
                      inputClassNames,
                      JSON.stringify( inputStyle ),
                      keyFieldWidth,
                      onResolveRowClass,
                      JSON.stringify( originalValue ), // check if originalValue really changed
                      presentation,
                      propertySet,
                      resolvedPlaceholder,
                      rowStyle,
                      showSorter,
                      user ? user.language : undefined,
                      valueFieldWidth,
                      valueToUseForCaching
                  ]
              );

        // use refs for dynamic resolving
        currentElementValue.current = elementValue;
        currentTransformedValue.current = transformedValue;
        currentPropertySet.current = propertySet;

        useEffect(
            () => {
                setElementValue( transformedValue );
                updateKeyToUniqueIdMapping( transformedValue );
            },
            [
                transformedValue
            ]
        );

        // stop AddNewEntry for grids if the new entry is contained in given value
        useEffect(
            () => {
                if ( ( presentation === 'grid' || presentation === 'grid_reversed' ) && addNewEntry && value && Object.values( value ).find( v => v.id === 0 ) ) {
                    setAddNewEntry( false );
                }
            },
            [
                addNewEntry,
                presentation,
                transformedValue
            ]
        );

        // mark chips invalid or dirty if
        useEffect(
            () => {
                if ( presentation === 'chips' && chipsComponentRef.current ) {
                    const chipsElement = chipsComponentRef.current.getElement();

                    for ( const chipId of Object.keys( chipsCache ) ) {
                        const chipElement = chipsElement.querySelector( `#${chipId}` )?.parentElement?.parentElement;

                        if ( chipElement ) {
                            chipElement.tabIndex = chipsCache[ chipId ].tabIndex;
                            chipElement.classList.remove( 'yn-chip-error', 'yn-chip-dirty' );

                            if ( chipsCache[ chipId ].errorMessage ) {
                                chipElement.classList.add( 'yn-chip-error' );
                            }
                            else if ( chipsCache[ chipId ].isDirty ) {
                                chipElement.classList.add( 'yn-chip-dirty' );
                            }

                            // rowStyle - what the entry resolved to last time has to go first, the
                            // condition may well have stopped matching
                            const appliedRowStyleClassNames = appliedChipRowStyleClassNames.current[ chipId ] ?? [];

                            for ( const className of appliedRowStyleClassNames ) {
                                if ( !chipsCache[ chipId ].rowStyleClassNames.includes( className ) ) {
                                    chipElement.classList.remove( className );
                                }
                            }
                            for ( const className of chipsCache[ chipId ].rowStyleClassNames ) {
                                chipElement.classList.add( className );
                            }

                            appliedChipRowStyleClassNames.current[ chipId ] = chipsCache[ chipId ].rowStyleClassNames;
                        }
                    }

                    // an entry that is gone takes its bookkeeping with it
                    for ( const chipId of Object.keys( appliedChipRowStyleClassNames.current ) ) {
                        if ( !chipsCache[ chipId ] ) {
                            delete appliedChipRowStyleClassNames.current[ chipId ];
                        }
                    }
                }
            },
            [
                presentation,
                elementValue,
                chipsCache
            ]
        );

        useLayoutEffect(
            () => {
                if ( presentation === 'chips' && chipsComponentRef.current ) {
                    const chipsInput = chipsComponentRef.current.getInput();

                    if ( chipsInput ) {
                        chipsInput.setAttribute( 'tabindex', `${tabIndex}` );
                    }
                }
            }
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            errorMessage={fieldWrapperErrorMessage}
            isDirty={fieldWrapperIsDirty}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            {fieldContent}
        </FieldWrapper>;
    }
};
EnumField.defaultProps = defaultProps;

export default EnumField;
